import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Ausgabeprofil, BelegAblage, Belegmodell, DatevStand, Position,
              PositionsStand, Rechnung, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung,
         useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { feldFehlerProp, folgewegFehler, kopfFehlerZahl } from "./AngebotDetail.js";
import { Belegvorschau } from "./Belegvorschau.js";
import { PositionsEditor, PlatzhalterTextfeld } from "./PositionsEditor.js";
import type { PositionsEditorClient } from "./PositionsEditor.js";
import { Kopfblock, useKopffaltung } from "./Kopffaltung.js";
import type { Kopffaltung } from "./Kopffaltung.js";
import { BelegParteien, PARTEIEN_KOPFFELDER } from "./BelegParteien.js";
import type { BelegParteienModalClient } from "./BelegParteien.js";
import { renderMarkdown } from "../markdown.js";
import { formatBetrag, formatDatum, formatDezimalBetrag,
         formatMenge } from "../format.js";
import { RECHNUNG_STATUS_LABEL } from "../vorgangsstatus.js";
import { PDF_HINWEIS_ART, apiMeldung, kopffeldAusFehler, oderNull, speichereBlob,
         speichereParteien, useParteienGriff } from "./vorgangsdetail.js";
import type { KopffeldTabelle, PdfMeldung } from "./vorgangsdetail.js";
// Die Klassennamen bleiben `krg__` (Etappe 1: an herausgelösten Bestandsteilen wird nichts
// umbenannt) — umgezogen ist nur die DATEI: Die Regeln dessen, was DIESER Baustein
// zeichnet, stehen jetzt hier statt im Stilblatt des Reiters. Damit bringt der Baustein
// seine Gestalt überallhin mit, ohne dass components/ nach views/ hineingreifen müsste
// (dieselbe Richtung wie bei AngebotDetail.css).
import { KiKnopf } from "./meHuellen.js";
import "./RechnungDetail.css";

/**
 * Das RECHNUNGSDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 5b).
 *
 * Herausgelöst aus views/KundeRechnungen.tsx nach dem Muster der Aufgabe 2 (AngebotDetail,
 * AuftragDetail, ArtikelDetail), damit der Akten-Reiter und der Rechnungs-BEREICH
 * buchstäblich DASSELBE Detail zeichnen — nicht zwei Masken, die sich anfangs gleichen und
 * mit dem dritten Fix auseinanderlaufen. Der Umbau ist rein strukturell: Es kommt kein
 * Verhalten hinzu und es fällt keines weg.
 *
 * Was der Baustein trägt — der VOLLE Bestand des Reiters am einzelnen Beleg: die Kopfzeile
 * (Nummer bzw. „Entwurf", Kürzel, Auftrag, Status), die Datenzeile (Bruttobetrag,
 * Rechnungsdatum), die Bezugszeilen zwischen Original und Korrektur samt ihren Sprüngen,
 * das Bearbeiten des Entwurfs (Kopffelder, abweichender Lieferort, Positions-Editor — seit
 * dem 06.09.2026 im strikten Modalpanel, siehe `offenerKasten`), die
 * nur-lesende Fassung des festgeschriebenen bzw. stornierten Belegs mit ihren
 * Markdown-Belegtexten, das Festschreiben samt Rückfrage-Kasten und Ablage-Ankündigung,
 * das Stornieren, das Anlegen einer Korrekturrechnung, die drei Ausgabewege (PDF,
 * XRechnung, ZUGFeRD) mitsamt der `fields[]`-Befundliste, die Ablage des Beleg-PDFs in der
 * Kundenakte und das Render-Modell als aufklappbarer JSON-Block.
 *
 * Was er NICHT weiss: dass es eine Kundenakte gibt. Die LISTE, der Meldungsstreifen und die
 * Frage, WELCHE Rechnung gerade aufgeschlagen ist, gehören dem Rahmen — er reicht sie als
 * Eigenschaften und Rückrufe herein (`offen`, `gesperrt`, `onOeffnen`/`onSchliessen`/
 * `onNeuLaden`/`onMeldung`/`onFortschreiben`/`onSpringeZuRechnung`).
 *
 * **Warum der Sprung ein RÜCKRUF ist.** Original und Korrektur zeigen aufeinander, und der
 * Klick auf „Zum Original" muss den anderen Beleg aufschlagen. Im Akten-Reiter heisst das:
 * eine andere Zeile derselben Liste öffnen; im Bereich: die ADRESSE auf `/rechnungen/<id>`
 * wechseln. Beides kann der Baustein nicht wissen — er prüft nur, was er allein weiss
 * und meldet dann nach oben.
 *
 * Der Beleg kommt als GELADENER Satz herein (`rechnung`), nicht als blosse Kennung: Der
 * Reiter führt seine Liste bereits, und ein Nachladen je Zeile wäre bei zwanzig Rechnungen
 * zwanzig zusätzliche Abrufe — eine Verhaltensänderung, die dieser Umbau ausdrücklich nicht
 * machen darf.
 *
 * Eigener CSS-Block `krg` — mit `kang` (Angebote), `kauf` (Aufträge), `brng` (Rechnungs-
 * Bereich) und `pe__` (Editor) wird KEINE Klasse geteilt (Kollisions-Lehre 01.09.).
 */

/** Nur die Klientenmethoden, die das DETAIL braucht — Liste und Auswahllisten bleiben beim
 *  Rahmen (siehe `RechnungenClient` in views/KundeRechnungen.tsx). Die Positionswege reicht
 *  es unverändert an den gemeinsamen Editor durch, mit `art="invoice"`. */
export type RechnungDetailClient = Pick<ApiClient,
  "updateInvoice" | "issueInvoice" | "cancelInvoice" | "createInvoiceKorrektur"
  // „Neu laden" im fremden Stand des Bearbeiten-Panels (ME-Etappe 3, Aufgabe 5): der
  // Serverstand DIESES Belegs, ohne die Liste des Rahmens zu bemühen.
  | "getInvoice"
  | "markInvoicePaid" | "unmarkInvoicePaid"
  | "invoiceRenderModel" | "renderInvoice" | "getInvoiceAblage" | "createInvoiceAblage"
  // Der DATEV-Stand dieses Belegs (Migration 028, Aufgabe 9) — ein eigener Lese-Weg,
  // dieselbe Bauart wie der Ablage-Stand darüber.
  | "getDatevStandZuRechnung"
  // Belegvorschau (24.09.2026): die Seitenbilder der Rechnung.
  | "vorschauInvoice">
  & PositionsEditorClient
  & BelegParteienModalClient;

/**
 * Die AUSWAHLHILFE, die jede Rechnungsansicht braucht: die Platzhalternamen für Legende und
 * Vorschau.
 *
 * Sie wird vom RAHMEN geladen (`useRechnungsUmfeld`) und hier hereingereicht, nicht je
 * Detail geholt: Sonst zöge jede Listenzeile ihren eigenen Abruf für Daten, die für alle
 * dieselben sind (dieselbe Überlegung wie bei `AngebotsUmfeld`).
 *
 * Die LÄNDERLISTE ist mit Adressen Etappe 3, Aufgabe 6 entfallen. Sie trug allein die
 * Länderauswahl des Lieferort-Aufklappers; seit der Lieferort eine ADRESSWAHL ist, fragt
 * niemand mehr danach — und ein Abruf, dessen Ergebnis keine Ansicht mehr liest, ist eine
 * Anfrage je Listenzeile für nichts.
 */
export type RechnungsUmfeld = {
  platzhalter: string[];
};

/**
 * Lädt die Auswahlhilfe einer Rechnungsansicht.
 *
 * Scheitert der Abruf, bleibt die Ansicht bedienbar: Dann fehlt nur die Legende, und die
 * Vorschau lässt alle Tokens stehen.
 */
export function useRechnungsUmfeld(
  client: Pick<ApiClient, "listPlaceholders">,
): RechnungsUmfeld {
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);

  useEffect(() => {
    let ab = false;
    client.listPlaceholders()
      .then(({ variablen }) => { if (!ab) setPlatzhalter(variablen); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  return { platzhalter };
}

/*
 * HIER STAND `RECHNUNGS_MELDUNGS_STIL` (Klasse und ARIA-Rolle je Meldungsart, Fund W1). Seit
 * ME-Etappe 3 (Aufgabe 5) trägt `Hinweis` (me-hinweis) beides selbst: Die Art des Elements
 * (`PDF_HINWEIS_ART` in vorgangsdetail.ts) entscheidet über Farbe UND Rolle — die Paarung,
 * die die Tabelle absichern sollte, gibt es nicht mehr von Hand.
 */

/** Nur ein ENTWURF ist änderbar — an allem anderen hängt eine gezogene Nummer. */
export function istEntwurf(r: Rechnung): boolean {
  return r.status === "DRAFT";
}

/**
 * Die drei Ausgabewege einer Rechnung — Knopfbeschriftung, Erfolgssatz und der Dateiname,
 * falls die Antwort keinen mitbringt. EINE Tabelle statt dreier gleichlautender Knöpfe:
 * Ein vierter Weg ist damit eine Zeile, und der Erfolgssatz kann nicht zum Profil
 * verrutschen (dieselbe Idee wie RECHNUNGS_MELDUNGS_STIL).
 *
 * Der Erfolgssatz nennt das ERGEBNIS, nicht den Knopf: Wer „Als XRechnung" geklickt hat
 * und danach „Das PDF wurde heruntergeladen" liest, glaubt zu Recht, den falschen Knopf
 * erwischt zu haben.
 */
const AUSGABEWEGE: Record<Ausgabeprofil,
  { knopf: string; ergebnis: string; rueckfallName: string }> = {
  PDF_A3: { knopf: "Als PDF", ergebnis: "Das PDF", rueckfallName: "rechnung.pdf" },
  XRECHNUNG_30: {
    knopf: "Als XRechnung", ergebnis: "Die XRechnung", rueckfallName: "rechnung.xml",
  },
  ZUGFERD_EN16931: {
    knopf: "Als ZUGFeRD", ergebnis: "Die ZUGFeRD-Rechnung",
    rueckfallName: "rechnung-zugferd.pdf",
  },
};

/** Die beiden E-Rechnungs-Wege — in der Reihenfolge der Knopfleiste, hinter „Als PDF". */
const E_RECHNUNGS_PROFILE: Ausgabeprofil[] = ["XRECHNUNG_30", "ZUGFERD_EN16931"];

/**
 * Was die Meldung der API bei fehlenden Pflichtwerten NICHT sagen kann: wo man sie pflegt.
 *
 * Die Engine nennt Feldpfade des Belegvertrags (`/absender/kontakt/telefon`) — richtig und
 * genau, aber niemand pflegt einen Feldpfad. Den Weg zur Maske kennt allein die
 * Oberfläche, deshalb steht er hier und nicht im Server. Angehängt wird er nur bei
 * PFLICHTWERTE_FEHLEN: Die andere 422 dieses Weges (PROFIL_UNZULAESSIG) trägt bereits
 * einen Satz, den man befolgen kann, und zwei Ratschläge nebeneinander sind einer zu viel.
 */
const PFLICHTWERTE_HINWEIS =
  " Angaben zum Verkäufer pflegen Sie unter Verwaltung → Unternehmen, Angaben zum "
  + "Empfänger in den Stammdaten des Kunden.";

/*
 * Die Dezimal- und Mengenformatierer stehen seit der Fixrunde 1 zu Aufgabe 7 in `format.ts`
 * (`formatDezimalBetrag`, `formatMenge`) — sie standen wortgleich auch im Positions-Editor
 * und in der Vorlagensicht. Die Summen der Positionsantwort kommen als ZEICHENKETTE, damit
 * unterwegs nichts gerundet wird; angezeigt wird daraus ein deutscher Betrag.
 */

/**
 * Der heutige Kalendertag als Vorgabe im Festschreib-Dialog.
 *
 * Aus den ÖRTLICHEN Feldern zusammengesetzt, nicht über `toISOString()`: Das wäre der
 * UTC-Tag, und nachts stünde der Vortag im Feld (dieselbe Falle, die am Server mit
 * `heuteAlsTag()` geschlossen wurde). Verbindlich ist ohnehin der Server — schickt die
 * Maske gar kein Datum, setzt er den Berliner Kalendertag.
 */
function heute(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const tt = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${tt}`;
}

/** Die Kopffelder als Formularwerte. Alle als Zeichenkette, damit ein geleertes Feld erst
 *  beim Absenden zu `null` wird. */
type Entwurf = {
  invoiceDate: string;
  servicePeriodStart: string;
  servicePeriodEnd: string;
  introText: string;
  outroText: string;
  /**
   * Der abweichende LIEFERORT (BG-13, Migration 015) — fünf Felder, alle wahlfrei.
   *
   * **In der MASKE stehen sie seit Adressen Etappe 3, Aufgabe 6 nicht mehr.** Der
   * Lieferort ist dort eine ADRESSWAHL im Kopfblock PARTEIEN geworden, und das
   * Render-Modell nimmt seit Aufgabe 5 die gewählte DELIVERY-Adresse VOR diesen Feldern.
   * Zwei Eingabewege für eine Anschrift hiessen: Die Maske zeigt die eine, das PDF druckt
   * die andere.
   *
   * Mitgeschickt werden sie WEITERHIN, unverändert, wie sie hereinkamen: Bis Migration 024
   * die Spalten fallen lässt, ist der Bestandswert der Rückweg für alles, was noch aus
   * ihnen liest (`lieferortAus` der Domäne, das Lese-Detail unten). Weggelassen würde
   * `updateInvoice` sie als „gelöscht" lesen — Vollersatz, siehe `kopfSpeichern`.
   */
  deliveryName: string;
  deliveryStreet: string;
  deliveryPostalCode: string;
  deliveryCity: string;
  /** ISO-Kürzel oder "" für „nicht angegeben" — anders als am Kunden gibt es hier KEINE
   *  Vorgabe DE: Der ganze Block ist wahlfrei, und ein Land ohne Ort wäre eine Angabe
   *  über nichts. */
  deliveryCountry: string;
  /** Das Rechnungsformat (Migration 025) — VOLLERSATZ wie der Lieferort und das Datum. */
  invoiceFormat: string;
};

/**
 * Die drei Rechnungsformate mit ihren deutschen Beschriftungen (Migration 025). Die
 * SCHLÜSSEL sind englisch und kommen aus dem Vertrag; die Wörter stehen hier, weil `web`
 * nichts aus `domain` importiert. Die Liste steht auch in KundeStammdaten.tsx — vier
 * Zeilen zweimal sind besser als ein neuer geteilter Block für eine Beschriftung.
 */
const RECHNUNGSFORMATE: { wert: string; text: string }[] = [
  { wert: "PDF_A3", text: "PDF" },
  { wert: "ZUGFERD_EN16931", text: "ZUGFeRD (PDF mit XML)" },
  { wert: "XRECHNUNG_30", text: "XRechnung (XML)" },
];

/**
 * Das gemeinsame Präfix der Feldkennungen im Bearbeiten-Panel („krg-datum", „krg-von", …).
 *
 * Es steht seit dem 11.09.2026 als Konstante da und nicht mehr nur abgetippt an jedem
 * `id`: Die Abbildung Servername → Feld-Id (`RECHNUNG_KOPFFELDER`) setzt genau dieses
 * Präfix davor. Zwei Schreibweisen desselben Präfixes wären ein Fokus, der ins Leere geht —
 * und zwar lautlos.
 */
const KOPF_PRAEFIX = "krg";

/** Die Kennung des eingefalteten Kopfblocks (`aria-controls`). */
const KOPF_INHALT_ID = `${KOPF_PRAEFIX}-kopfdaten`;

/**
 * SERVER-FELDNAME → Endung der Feld-Id, für die Felder im eingefalteten Kopfblock
 * (Bauart und Begründung: `KopffeldTabelle` in vorgangsdetail.ts).
 *
 * Die Rechnung hat KEINE Prüfung in der Maske — anders als Angebot und Auftrag kennt sie
 * kein Pflichtfeld, das die Oberfläche selbst beurteilen könnte (auch ein Entwurf ohne
 * Rechnungsdatum ist zulässig; der Server setzt beim Festschreiben den Kalendertag). Die
 * Befunde kommen deshalb ALLE vom Server, als 422 mit `ApiError.fields[]` aus
 * `validateInvoiceKopf` (domain/invoiceRules.ts) — und genau die stehen hier.
 *
 * Was NICHT darin steht, ist absichtlich nicht darin: `introText` und `outroText` stehen
 * AUSSERHALB des Kopfblocks und sind immer zu sehen; ein Befund an ihnen darf den Block
 * nicht aufreissen. `paymentTermText` fehlt ebenfalls — die Maske schickt ihn gar nicht mit
 * (er ist der Snapshot der Ableitung), es gibt also kein Feld, auf das man zeigen könnte.
 *
 * Die fünf LIEFERORT-Felder standen bis Adressen Etappe 3, Aufgabe 6 mit drin. Sie sind
 * hier gefallen, weil ihre Feld-Kennungen gefallen sind: Der Lieferort ist jetzt eine
 * ADRESSWAHL im Kopfblock PARTEIEN (`deliveryAddressId`), die fünf Textfelder gibt es in
 * der Maske nicht mehr. Eine Kennung, die auf kein Feld mehr zeigt, klappte den Block auf
 * und fokussierte nichts — ein unsichtbarer Fehler mit zusätzlichem Aufwand.
 */
export const RECHNUNG_KOPFFELDER: KopffeldTabelle = {
  invoiceDate: "datum",
  servicePeriodStart: "von",
  servicePeriodEnd: "bis",
  invoiceFormat: "rechnungsformat",
  // Die Felder des Kopfblocks PARTEIEN (Adressen Etappe 3) — sie stehen IM Kopfblock und
  // werden in einem zweiten Weg gespeichert (`setBelegParteien`). Ein Befund von dort muss
  // denselben Weg ins Bild finden wie einer am Rechnungsdatum.
  ...PARTEIEN_KOPFFELDER,
};

/** Den Kopfblock auf das Feld öffnen, das ein Befund des SERVERS nennt (422 mit
 *  `ApiError.fields[]`). Trägt der Fehler keine Befunde oder nur Felder ausserhalb des
 *  Blocks, bleibt der Block zu — Bauart wie an Angebot und Auftrag. */
function zeigeRechnungsFehlerFeld(kopf: Kopffaltung, fehler: unknown): void {
  const ziel = kopffeldAusFehler(RECHNUNG_KOPFFELDER, KOPF_PRAEFIX, fehler);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

/** Trägt ein 422-Befund ein Feld, das im (vielleicht zugeklappten) Kopfblock steht? Dann
 *  klappt die Maske ihn im SELBEN Zug auf, in dem der Befund ans Feld kommt — der Sprung
 *  des Formulars zum ersten Fehler findet es danach sichtbar vor (Review-Fokus 3). */
function betrifftKopfblock(fehler: unknown): boolean {
  return fehler instanceof ApiError && fehler.status === 422
    && (fehler.fields ?? []).some((f) => RECHNUNG_KOPFFELDER[f.field] !== undefined);
}

/**
 * Die Beschriftungen je SERVER-Feldname für die Sammelmeldung des Formularzustands
 * (`useFormularZustand.beschriftungen`, Ruling Aufgabe 3/4): Ein Befund, der an keinem Feld
 * steht, nennt die Beschriftung, die der Benutzer sieht — nie den technischen Namen. Die
 * fünf Lieferort-Spalten gehen noch mit (Vollersatz bis Migration 024), stehen aber in
 * keinem Feld mehr: Ein Befund an ihnen lautet „Lieferort: …".
 */
export const RECHNUNG_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  invoiceDate: "Rechnungsdatum", servicePeriodStart: "Leistungszeitraum von",
  servicePeriodEnd: "Leistungszeitraum bis", invoiceFormat: "Rechnungsformat",
  introText: "Einleitungstext", outroText: "Ausleitungstext",
  deliveryName: "Lieferort", deliveryStreet: "Lieferort", deliveryPostalCode: "Lieferort",
  deliveryCity: "Lieferort", deliveryCountry: "Lieferort",
};

/** Die Felder mit eigenem Fehlerplatz im Bearbeiten-Panel — ein Befund zu ihnen steht AM
 *  FELD; alles andere (Texte, Lieferort-Spalten) als „<Beschriftung>: <Satz>" am Fuss. */
const RECHNUNG_FELDER_AM_FELD: readonly string[] =
  ["invoiceDate", "servicePeriodStart", "servicePeriodEnd", "invoiceFormat",
   // Die Felder des Kopfblocks PARTEIEN — ein Befund an ihnen steht im Block AM FELD
   // (die Maske reicht die Befunde des Formularzustands an `BelegParteien` weiter).
   ...Object.keys(PARTEIEN_KOPFFELDER)];

/** Die Beschriftungen der beiden Kästen (Festschreiben, Bezahlt) — ihr je einziges Feld. */
const KASTEN_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  invoiceDate: "Rechnungsdatum", paidAt: "Bezahlt am",
};

/**
 * Ein Schreibweg mit seinem eigenen Rückfallsatz: Der Formularzustand bildet die Meldung
 * aus `Error.message` — ohne diesen Mantel stünde bei einem Netzabbruch „Das Speichern ist
 * fehlgeschlagen." am Festschreiben. 409 und 422 gehen UNVERÄNDERT durch, an ihnen hängen
 * der fremde Stand und die Feldbefunde.
 */
async function mitRueckfall<T>(lauf: () => Promise<T>, rueckfall: string): Promise<T> {
  try {
    return await lauf();
  } catch (e) {
    if (e instanceof ApiError && (e.status === 409 || e.status === 422)) throw e;
    throw new Error(apiMeldung(e, rueckfall));
  }
}

function entwurfAus(r: Rechnung): Entwurf {
  return {
    invoiceDate: r.invoiceDate ?? "",
    servicePeriodStart: r.servicePeriodStart ?? "",
    servicePeriodEnd: r.servicePeriodEnd ?? "",
    introText: r.introText ?? "",
    outroText: r.outroText ?? "",
    deliveryName: r.deliveryName ?? "",
    deliveryStreet: r.deliveryStreet ?? "",
    deliveryPostalCode: r.deliveryPostalCode ?? "",
    deliveryCity: r.deliveryCity ?? "",
    deliveryCountry: r.deliveryCountry ?? "",
    invoiceFormat: r.invoiceFormat,
  };
}

/**
 * Eine Kennung oder ein Wert der Lese-Gestalt als „gibt es" / „gibt es nicht" — für die
 * beiden Richtungen des KORREKTUR-Bezugs (Migration 017).
 *
 * `undefined` zählt wie `null`, und das ist dieselbe Nachsicht, die bis Adressen Etappe 3
 * auch `hatLieferort` walten liess: Ein
 * Aufrufer, der das Feld gar nicht führt (eine Testattrappe, ein älterer Klient), meint
 * „keinen Bezug" — nicht „einen Bezug auf undefined". Ohne diese Stelle stünde an jeder
 * gewöhnlichen Rechnung eine Bezugszeile mit „Invalid Date".
 */
function alsBezug(wert: string | null | undefined): string | null {
  return wert === null || wert === undefined || wert === "" ? null : wert;
}

export type RechnungDetailProps = {
  client: RechnungDetailClient;
  kundeId: string;
  /** Der Beleg — fertig geladen, samt seiner `version` (siehe Kopf der Datei). */
  rechnung: Rechnung;
  /** Die Unternehmen mit Kundenbeziehung — allein zur Auflösung des Kürzels und für die
   *  Platzhalter-Vorschau. Angelegt wird an einer Rechnung nichts. */
  companies: Unternehmen[];
  /** Die vom Rahmen geladenen Auswahlhilfen (`useRechnungsUmfeld`). */
  umfeld: RechnungsUmfeld;
  /** Steht GENAU DIESE Rechnung gerade aufgeschlagen? Die Entscheidung gehört dem Rahmen —
   *  nur so bleibt es bei einem offenen Kasten je Ansicht. */
  offen: boolean;
  /** Ist die Bedienung gesperrt, weil anderswo eine Rechnung offen steht? */
  gesperrt: boolean;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`).
   *  Fehlt er, bleibt das Token in der Vorschau sichtbar stehen. */
  kundeName?: string | undefined;
  /** „Ansehen" am geschlossenen Eintrag — es schlägt das LESE-Detail auf. Das Formular
   *  öffnet erst „Bearbeiten" darin (siehe `bearbeitung`). */
  onOeffnen: () => void;
  onSchliessen: () => void;
  /** Der Rahmen soll seine Liste neu holen (nach 409, nach dem Anlegen einer Korrektur). */
  onNeuLaden: () => void | Promise<unknown>;
  /** Der Meldungsstreifen gehört dem Rahmen — er steht über der ganzen Ansicht, nicht am
   *  einzelnen Beleg. Die ART reist mit (siehe RECHNUNGS_MELDUNGS_STIL): Ein gescheitertes
   *  Festschreiben darf nicht aussehen wie ein gelungenes. */
  onMeldung: (meldung: PdfMeldung | null) => void;
  /** Der Beleg ist weitergedreht (Version und Beträge aus dem Positions-Editor, frischer
   *  Stand nach einem Schreibweg): Der Rahmen schreibt ihn in seine Liste fort und reicht
   *  ihn als `rechnung` zurück herein. Ohne das rechneten die nächsten Knöpfe mit einer
   *  veralteten Version und meldeten „zwischenzeitlich geändert" für die eigene Änderung
   *  (Lehre aus dem Produktfix f746859). */
  onFortschreiben: (rechnung: Rechnung) => void;
  /**
   * Der SPRUNG zwischen Original und Korrektur — in beide Richtungen.
   *
   * Als Rückruf und nicht als eigener Zustand: Der Akten-Reiter öffnet die andere Zeile
   * seiner Liste, der Rechnungs-Bereich wechselt die Adresse auf `/rechnungen/<id>`.
   */
  onSpringeZuRechnung: (rechnungId: string) => void;
  /**
   * Eine frisch angelegte KORREKTURRECHNUNG (bzw. die schon bestehende, die die API
   * stattdessen zurückgibt) — samt dem Satz, der sagt, was gerade geschehen ist.
   *
   * Ein eigener Rückruf und nicht der Sprung oben, weil der Rahmen hier ZWEIERLEI tun muss
   * und nur er beides kann: seine Liste neu holen (es haben sich zwei Sätze geändert — die
   * Korrektur ist dazugekommen, und das Original trägt nun den Rückverweis) und den neuen
   * Beleg AUFNEHMEN, falls die frische Liste ihn wider Erwarten noch nicht führt. Der Satz
   * reist deshalb mit: Ohne ihn stünde der Bearbeiter nach einem erfolgreichen Klick vor
   * „nicht gefunden".
   */
  onKorrekturAngelegt: (korrektur: Rechnung, hinweis: string) => void | Promise<unknown>;
  /**
   * Das BEARBEITEN-Panel über dem Entwurf ist auf- oder zugegangen.
   *
   * Den Zustand führt der Baustein selbst — er gehört zum Formular, nicht zur Liste (siehe
   * `bearbeitung` unten). Gemeldet wird er trotzdem, weil ein Rahmen daran seinen
   * VERLASSEN-WÄCHTER hängen kann: Eingaben verlieren kann eine Navigation nur, solange das
   * FORMULAR steht. Am blossen Lese-Detail dahinter gibt es nichts zu verlieren, und eine
   * Rückfrage, die trotzdem käme, würde ungelesen weggeklickt.
   *
   * WAHLFREI, weil ihn nur ein Rahmen mit Wächter braucht: Der Rechnungs-Bereich hat einen,
   * der Akten-Reiter nicht. Ein Pflicht-Rückruf zwänge dort eine leere Funktion — genau der
   * tote Prop, den die Hausregel vermeiden will.
   */
  onBearbeitungWechsel?: (offen: boolean) => void;
};

export function RechnungDetail(p: RechnungDetailProps): ReactElement {
  const { client, kundeId, rechnung, companies, umfeld, offen, gesperrt } = p;
  const r = rechnung;

  /** Die Kopffelder eines ENTWURFS. `null` am festgeschriebenen Beleg — dort gibt es kein
   *  Formular (siehe `belegKasten`). */
  const [entwurf, setEntwurf] = useState<Entwurf | null>(
    () => (istEntwurf(r) ? entwurfAus(r) : null));
  /** Der Positionsstand: am ENTWURF meldet ihn der Editor nach jedem Schritt, am
   *  festgeschriebenen Beleg wird er einmal gelesen. */
  const [positionen, setPositionen] = useState<PositionsStand | null>(null);
  /** Der Fehlschlag beim LESEN der Zeilen eines festgeschriebenen Belegs (`zeilenLesen`).
   *  Die Befunde des Speicherns führt seit ME-Etappe 3 der Formularzustand (`kopfFormular`). */
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  /** Der Entwurf des Kopfblocks PARTEIEN — Bauart und Begründung bei `useParteienGriff`
   *  (vorgangsdetail.ts). */
  const parteien = useParteienGriff();
  /** Läuft gerade eine Aktion an DIESEM Beleg (sperrt seine Knöpfe)? */
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  /**
   * Steht das BEARBEITEN-Panel über dem aufgeschlagenen Entwurf?
   *
   * Der Unterschied zu `offen` ist seit der Modal-Hausregel (06.09.2026) wesentlich und
   * gehört deshalb HIERHER und nicht zum Rahmen: `offen` heisst „das Detail ist
   * aufgeschlagen" — Bezugszeilen, Ausgabewege, Ablagestand, Render-Modell, Aktionen. Das
   * BEARBEITEN ist ein Panel darüber, und es hat genau zwei Ausgänge; hinter beiden liegt
   * weiterhin das Lese-Detail und nicht die blosse Liste.
   *
   * Ohne diese Trennung wäre am Entwurf nur zu wählen zwischen „Formular offen" und
   * „Eintrag zu": Der Weg zu „Als PDF", zum Render-Modell und zum Festschreiben liefe dann
   * durch ein Formular hindurch, das man erst schliessen muss — und beim Schliessen wäre
   * auch das Lese-Detail weg. Genau daran ist die erste Fassung dieses Umbaus gescheitert.
   *
   * **Aufschlagen öffnet KEIN Formular** (Fixrunde 1, 07.09.2026). Das war der zweite
   * Anlauf noch anders — ein Entwurf sprang beim Aufschlagen gleich ins Bearbeiten. Das ist
   * aus zwei Gründen falsch: Der Zeilenknopf verspräche „Bearbeiten" und lieferte ein
   * Panel über einem Detail, das man gar nicht gesehen hat; und alles am Lese-Detail wäre
   * nur über einen Umweg erreichbar. Angebot und Auftrag halten es beim ZEILENKLICK
   * genauso (Detail sichtbar, „Bearbeiten" öffnet das Panel); ihre Ketten-Ableitungen
   * (Vorlage angewandt, Angebot→Auftrag) springen dagegen bewusst direkt ins Formular —
   * dort gibt es noch kein Detail zu sehen. Wer einen BESTEHENDEN Entwurf ändern will,
   * sieht ihn also erst und öffnet dann sein Formular.
   */
  const [bearbeitung, setBearbeitung] = useState(false);

  /**
   * Die EINFALTUNG der Kopffelder (components/Kopffaltung.tsx, Bonuspunkt 11.09.2026) —
   * dieselbe Gestalt wie an Angebot und Auftrag.
   *
   * Sie beginnt ZU: Die Rechnung wird an ihren POSITIONEN bearbeitet, und die standen bis
   * hierher unter Rechnungsdatum, Zahlungsbedingung, Leistungszeitraum, Lieferort und zwei
   * Textfeldern. Ein ANLEGEN gibt es an der Rechnung nicht — sie entsteht ausschliesslich
   * am Auftrag („Rechnung erzeugen", createInvoiceFromOrder) und kommt dort fertig
   * abgeleitet heraus. Deshalb steht hier nur der eine Startzustand, wo Angebot und Auftrag
   * zwei tragen (Bearbeiten zu, Anlegen auf).
   */
  const kopf = useKopffaltung(false);

  /** Zählt hoch, wenn „Neu laden" im fremden Stand den Serverstand holt — im `key` von
   *  Positions-Editor und Parteien: Sie führen ihren Stand selbst und laden so frisch. */
  const [standNeuLaden, setStandNeuLaden] = useState(0);

  /**
   * Die Formularzustände des BEARBEITEN-Panels (ME-Etappe 3, Aufgabe 5) — dieselbe Bauart
   * wie an Angebot und Auftrag: Eine 409 setzt den „fremden Stand" (die Eingaben bleiben
   * lesbar stehen, Speichern ist zu, „Neu laden" holt den Serverstand), statt das Panel zu
   * schliessen und still nachzuladen; eine 422 legt die Befunde an die Felder, klappt den
   * Kopfblock im selben Zug auf und springt zum ersten.
   */
  const kopfFormular = useFormularZustand<Rechnung>({
    speichern: () => kopfSchreiben(),
    neuLaden: async () => {
      const frisch = await client.getInvoice(kundeId, r.id);
      p.onFortschreiben(frisch);
      parteien.zuruecksetzen();
      setPositionen(null);
      setStandNeuLaden((n) => n + 1);
      if (istEntwurf(frisch)) {
        setEntwurf(entwurfAus(frisch));
        return;
      }
      // Ein anderer hat den Entwurf inzwischen festgeschrieben: Es gibt nichts mehr zu
      // bearbeiten — das Panel geht zu, dahinter steht das Lese-Detail des frischen Belegs,
      // und der Satz sagt, warum.
      setEntwurf(null);
      setBearbeitung(false);
      p.onMeldung({
        text: "Diese Rechnung ist inzwischen festgeschrieben — sie lässt sich nicht mehr "
          + "bearbeiten.",
        art: "hinweis",
      });
    },
    bekannteFelder: RECHNUNG_FELDER_AM_FELD,
    beschriftungen: RECHNUNG_BESCHRIFTUNGEN,
  });
  const speichert = kopfFormular.laeuft;

  /** Der Schreibweg des offenen Kastens (Festschreiben ODER Bezahlt — nie beide). */
  const kastenWeg = useRef<() => Promise<Rechnung>>(
    async () => { throw new Error("Kein Kasten offen."); });
  /**
   * Die Formularzustände der beiden KÄSTEN am Lese-Detail (Festschreiben, Bezahlt-Datum).
   * Sie tragen je ein Feld, also sind sie Formulare: Während des Aufrufs ist das Datum
   * gesperrt, eine 409 sperrt den Kasten mit „Neu laden" (der Rahmen holt den frischen
   * Stand, die Eingabe bleibt stehen), ein Befund steht am Feld.
   */
  const kastenFormular = useFormularZustand<Rechnung>({
    speichern: () => kastenWeg.current(),
    neuLaden: async () => { await p.onNeuLaden(); },
    bekannteFelder: ["invoiceDate", "paidAt"],
    beschriftungen: KASTEN_BESCHRIFTUNGEN,
  });

  /** Der Schreibweg der Aktion ohne Feld (Stornieren, Bezahlt-Kennzeichen zurücknehmen). */
  const aktionWeg = useRef<() => Promise<Rechnung>>(
    async () => { throw new Error("Keine Aktion gewählt."); });
  /**
   * Die Zustände der Aktionen OHNE Feld — Stornieren und das Zurücknehmen des
   * Bezahlt-Kennzeichens (Bestätigungsfrage, kein Kasten). Auch sie laufen
   * über den Formularzustand (Spec, Rückstand 6): Eine 409 sperrt die Aktionen dieses Belegs
   * und bietet „Neu laden" an, statt still nachzuladen; ein Fehlschlag steht am Beleg.
   */
  const { bestaetigen, bestaetigung } = useBestaetigung();

  /** Die Ja/Nein-Fragen vor Stornieren, Zurücknehmen und Korrektur — striktes Modal statt
   *  `window.confirm` (Aufgabe 8). */
  const aktionFormular = useFormularZustand<Rechnung>({
    speichern: () => aktionWeg.current(),
    neuLaden: async () => { await p.onNeuLaden(); },
  });
  /** Sind die Aktionen dieses Belegs zu? Während eines Aufrufs (Korrektur, Ablage, eine
   *  Aktion ohne Feld) und im fremden Stand bis „Neu laden" — dann nennt sie der Hinweis
   *  über den Knöpfen (`aktionsHinweise`). */
  const belegt = beschaeftigt || aktionFormular.zustand !== "bereit";
  /** Den Fehlschlag einer Aktion ohne Feld räumen, sobald am Beleg etwas Neues beginnt
   *  (Review A5, H3) — sonst stünde der Alarm über einem Beleg, an dem der nächste Schritt
   *  längst gelungen ist. Der fremde Stand bleibt: Ihn hebt nur „Neu laden“ auf. */
  function aktionsMeldungRaeumen(): void {
    if (aktionFormular.zustand === "bereit" && aktionFormular.meldung !== null) {
      aktionFormular.zuruecksetzen();
    }
  }

  /**
   * Ein je INSTANZ eindeutiges Präfix für die Feldkennungen der beiden Kästen
   * (Festschreiben, Bezahlt).
   *
   * Nur für die beiden — und der Unterschied ist der Grund: Der Entwurfs-Kasten mit seinen
   * festen Kennungen (`krg-datum`, `krg-von`, die Lieferort-Felder) kann je Ansicht nur
   * EINMAL offen stehen; dafür sorgt der Rahmen über `offen`/`gesperrt`. Die beiden
   * DIALOGE dagegen lassen sich am GESCHLOSSENEN Eintrag öffnen, wo nichts die übrigen
   * Zeilen sperrt: Im Akten-Reiter kann so an zwei Belegen zugleich ein Datumsfeld stehen —
   * mit derselben festen Kennung wären es zwei Elemente mit gleicher `id`, und ein Klick
   * auf die zweite Beschriftung setzte den Fokus ins Feld der ERSTEN Rechnung.
   */
  const feldId = useId();

  /** Steht der Festschreib-Kasten offen? Die VERSION wird erst beim Absenden aus dem
   *  hereingereichten Satz gelesen, nicht beim Öffnen: Zwischen beidem kann ein
   *  Positionsschritt gelegen haben. */
  const [festschreibOffen, setFestschreibOffen] = useState(false);
  const [festschreibDatum, setFestschreibDatum] = useState("");

  /** Steht der Bezahlt-Kasten offen (Migration 018)? Wie beim Festschreiben ein eigener
   *  Kasten und keine blosse Bestätigungsfrage, weil er ein FELD trägt — das Zahlungsdatum.
   *  Die VERSION wird auch hier erst beim Absenden aus dem hereingereichten Satz gelesen. */
  const [bezahltOffen, setBezahltOffen] = useState(false);
  const [bezahltDatum, setBezahltDatum] = useState("");

  const [renderModell, setRenderModell] = useState<Belegmodell | null>(null);
  const [renderFehler, setRenderFehler] = useState<string | null>(null);
  /**
   * Die ABLAGE des Beleg-PDFs in der Kundenakte — DREI Zustände, und alle drei tragen
   * Bedeutung:
   *
   *  - `undefined`: noch nicht beantwortet (frisch aufgeschlagen, oder die Abfrage ist
   *    fehlgeschlagen). Dann steht KEINE Zeile — weder „abgelegt" noch „nicht abgelegt";
   *    behaupten lässt sich beides nicht.
   *  - `null`: es liegt nichts in der Akte. Das ist der sichtbare Fall des klemmenden
   *    Automatiklaufs, und genau deshalb sagt die Maske es.
   *  - ein Stand: Kennung, Name und Zeitpunkt der letzten Ablage.
   *
   * Sie wird beim Aufschlagen einer festgeschriebenen Rechnung EINMAL geholt und nicht aus
   * der Rechnung gelesen: Es gibt kein Ablage-Feld an `Rechnung`, und es soll auch keines
   * geben — der Stand ist abgeleitet (die Datei gibt es oder nicht), und ein Feld daneben
   * würde beim ersten Löschen der Datei falsch.
   */
  const [ablage, setAblage] = useState<BelegAblage | null | undefined>(undefined);
  /** Die Rückmeldung der Ausgabe- und Ablage-Knöpfe — MIT ihrer Art. */
  const [engineMeldung, setEngineMeldung] = useState<PdfMeldung | null>(null);
  /** Ist die Vorschau offen? Sie hält keinen eigenen Stand über das Schliessen hinaus —
   *  ein `boolean` genügt, die Bilder holt sie sich selbst (Spec). */
  const [vorschauOffen, setVorschauOffen] = useState(false);

  /**
   * Der DATEV-STAND dieses Belegs (Migration 028, Aufgabe 9) — DIESELBEN drei Zustände wie
   * der Ablagestand oben: `undefined` heisst „noch nicht beantwortet" (keine Zeile),
   * `null` heisst „keine Kopplung für dieses Unternehmen" (schweigt ebenfalls — eine
   * Zeile über einen Weg, den dieses Unternehmen gar nicht geht, wäre Lärm), ein Stand
   * trägt Status, Fehlertext und Zeitstempel.
   */
  const [datev, setDatev] = useState<DatevStand["datev"] | null | undefined>(undefined);

  /**
   * Der Übergang „schlägt auf" bzw. „klappt zu": Beim Öffnen wird der Entwurf frisch aus
   * dem Beleg gebildet, und alles, was zum vorigen Anlauf gehörte, verfällt.
   *
   * Bewusst WÄHREND des Zeichnens statt in einem Effekt (React: „Adjusting state when a
   * prop changes"): Ein Effekt liefe erst nach dem ersten Bild, und für einen Augenblick
   * stünden die Werte des letzten Anlaufs in der Maske. Er ersetzt `oeffnen`/`schliessen`
   * des Reiters — dort setzten dieselben Zeilen den gemeinsamen Zustand zurück.
   */
  const [zuletztOffen, setZuletztOffen] = useState(offen);
  if (offen !== zuletztOffen) {
    setZuletztOffen(offen);
    setEntwurf(istEntwurf(r) ? entwurfAus(r) : null);
    // Aufschlagen und Zuklappen führen BEIDE aufs Lese-Detail — nie in ein Formular (siehe
    // `bearbeitung` oben). Das gilt auch für einen Beleg, den der Rahmen von aussen
    // aufschlägt (der Sprung aus dem Auftrag, das Aufnehmen einer Korrektur).
    setBearbeitung(false);
    kopf.zuruecksetzen();
    setLadeFehler(null);
    kopfFormular.zuruecksetzen();
    kastenFormular.zuruecksetzen();
    setFestschreibOffen(false);
    setBezahltOffen(false);
    setRenderModell(null);
    setRenderFehler(null);
    setEngineMeldung(null);
    setVorschauOffen(false);
    // Der Stand der VORIGEN Rechnung darf nicht stehen bleiben, und der Ablagestand geht
    // zurück auf UNBEKANNT — nicht auf „nichts abgelegt": erst fragen, dann sagen.
    setPositionen(null);
    setAblage(undefined);
    setDatev(undefined);
  }

  /*
   * KEIN KONTEXTWECHSEL-BLOCK HIER, und das ist Absicht (Fixrunde 1, 11.09.2026).
   *
   * Ein anderer Beleg an derselben Stelle wäre der Fall, in dem ein Zustand von A auf B
   * wanderte — der Entwurf mit seinen Feldwerten, ein offenes Panel, die Einfaltung des
   * Kopfblocks. Beide Verwender schliessen ihn jedoch an der WURZEL aus, statt jeden
   * Zustand einzeln nachzuräumen: `BereichRechnungen` gibt dem Baustein `key={satz.id}`,
   * der Akten-Reiter zeichnet ohnehin je Listenzeile eine eigene Instanz. Ein anderer Beleg
   * ist damit eine andere Instanz, und die beginnt mit allen elf Zuständen im
   * Startzustand.
   *
   * Ein zusätzlicher Riegel hier wäre nicht bloss überflüssig, er wäre GEFÄHRLICH: Er käme
   * nie zum Zuge und wüchse deshalb nicht mit — sein erster Einsatz (falls der Schlüssel je
   * fiele) hinterliesse eine halb zurückgesetzte Maske, weil er nur die Zustände kennte,
   * die es an seinem Schreibtag gab. Die Zusage gehört dorthin, wo sie geprüft wird: an den
   * Rahmen (bereichRechnungen.test.tsx, „der Baustein wird je Beleg neu aufgebaut").
   *
   * Was der Baustein SELBST zurücksetzt, steht im Block darüber: das Auf- und Zuklappen
   * seines Eintrags (`offen`) — der eine Wechsel, den er wirklich erlebt.
   */

  /*
   * Der Stand des Panels nach oben — für den Verlassen-Wächter des Rahmens (siehe
   * `onBearbeitungWechsel`). Als Effekt und nicht an den Setzstellen: Eine davon liegt im
   * Übergangsblock oben, der WÄHREND des Zeichnens läuft — dort den Zustand des Rahmens zu
   * setzen wäre ein Aufruf mitten im Render.
   *
   * Die Aufräumfunktion meldet das Zugehen auch dann, wenn dieser Baustein verschwindet
   * (der Bereich zeichnet je Adresse ein anderes Detail): Ohne sie bliebe der Wächter des
   * Rahmens an einem Formular hängen, das es nicht mehr gibt.
   */
  const onBearbeitungWechsel = p.onBearbeitungWechsel;
  useEffect(() => {
    onBearbeitungWechsel?.(bearbeitung);
    if (!bearbeitung) return;
    return () => { onBearbeitungWechsel?.(false); };
  }, [bearbeitung, onBearbeitungWechsel]);

  /**
   * Schlägt der Rahmen eine ANDERE Rechnung auf, fällt der hier offene Festschreib-Kasten
   * zu. Den Kasten führt seit dem Umzug in diesen Baustein er selbst; im Akten-Reiter hing
   * er früher an einem gemeinsamen Zustand über allen Zeilen, den es dort nicht mehr gibt.
   *
   * Der Kasten kann am GESCHLOSSENEN Eintrag stehen — er ersetzt dort die Knopfleiste —,
   * und ohne diese Zeile bliebe er neben einem fremden, aufgeschlagenen Beleg stehen.
   */
  const [zuletztGesperrt, setZuletztGesperrt] = useState(gesperrt);
  if (gesperrt !== zuletztGesperrt) {
    setZuletztGesperrt(gesperrt);
    if (gesperrt && !offen) {
      setFestschreibOffen(false);
      setBezahltOffen(false);
      kastenFormular.zuruecksetzen();
    }
  }

  /**
   * Die Zeilen einer festgeschriebenen Rechnung — EIN Lesezugriff, kein Editor. `listItems`
   * ist auch an einem eingefrorenen Beleg erlaubt (der Riegel sitzt an den Schreibwegen);
   * die Summen kommen fertig vom Server, hier wird keine Zahl gebildet.
   */
  const zeilenLesen = useCallback(async (id: string): Promise<void> => {
    try {
      setPositionen(await client.listItems({ art: "invoice", customerId: kundeId,
        vorgangId: id }));
    } catch (e) {
      setLadeFehler(apiMeldung(e, "Die Positionen konnten nicht geladen werden."));
    }
  }, [client, kundeId]);

  /**
   * Der Ablagestand der aufgeschlagenen Rechnung. Nur am festgeschriebenen Beleg — am
   * Entwurf gibt es ihn nicht (die Ablage hängt an der Festschreibung), und ein Aufruf
   * dafür wäre eine Anfrage, deren Antwort feststeht. Schlägt er fehl, bleibt der Stand
   * UNBEKANNT (`undefined`) — ein Ladefehler ist keine Auskunft über die Akte.
   */
  const ablageLesen = useCallback(async (id: string): Promise<void> => {
    try {
      const { ablage: stand } = await client.getInvoiceAblage(kundeId, id);
      setAblage(stand);
    } catch {
      setAblage(undefined);
    }
  }, [client, kundeId]);

  /**
   * Der DATEV-Stand der aufgeschlagenen Rechnung — ebenfalls nur am festgeschriebenen
   * Beleg (Entwurf und Storno vor der ersten Festschreibung kennen keinen Übergabeposten;
   * die Zeile zeigt sich ohnehin nur ab ISSUED/CANCELLED, siehe `r.status !== "DRAFT"`
   * unten). Ein Ladefehler bleibt wie beim Ablagestand UNBEKANNT (`undefined`) — kein
   * echter Fehlschlag darf als „keine Kopplung" (`null`) missverstanden werden.
   */
  const datevLesen = useCallback(async (id: string): Promise<void> => {
    try {
      const { datev: stand } = await client.getDatevStandZuRechnung(kundeId, id);
      setDatev(stand);
    } catch {
      setDatev(undefined);
    }
  }, [client, kundeId]);

  /*
   * Was das Aufschlagen eines BELEGS nach sich zieht: seine Zeilen, sein Ablagestand und
   * sein DATEV-Stand.
   *
   * Am Entwurf passiert hier nichts — dort führt der Positions-Editor die Zeilen, und eine
   * Ablage gibt es noch nicht. Der Status steht in den Abhängigkeiten, weil genau er sich
   * unter dem offenen Kasten ändern kann: Nach dem Festschreiben wird aus dem Entwurf ein
   * Beleg, und dann müssen alle drei Abrufe nachgeholt werden.
   */
  useEffect(() => {
    if (!offen || r.status === "DRAFT") return;
    void zeilenLesen(r.id);
    void ablageLesen(r.id);
    void datevLesen(r.id);
  }, [offen, r.id, r.status, zeilenLesen, ablageLesen, datevLesen]);

  /**
   * Dasselbe für einen POSITIONS-Schritt: Der Editor meldet die frische Version des Vorgangs
   * und den ganzen Stand. Beides muss auch in die LISTE des Rahmens — genau hier lag die
   * Lücke hinter dem Produktfix f746859.
   */
  function positionenGeaendert(version: number, standNeu: PositionsStand): void {
    setPositionen(standNeu);
    // Der Bruttobetrag der Liste wandert mit: Er steht in der Antwort des Editors
    // (`steuersummen.gesamtBrutto`, vom Server gerechnet) und wäre sonst bis zum nächsten
    // Laden der Liste der Stand von vorhin. NICHT hier ausgerechnet — nur übernommen.
    const brutto = Number(standNeu.steuersummen.gesamtBrutto);
    p.onFortschreiben({
      ...r,
      version,
      netAmount: standNeu.netAmount,
      ...(Number.isFinite(brutto) ? { grossAmount: brutto } : {}),
    });
  }

  /**
   * Trägt der Kopf ungespeicherte Änderungen? Die Felder, die `entwurfAus` führt, flach
   * verglichen.
   *
   * Gebraucht wird das zweimal: für die Rückfrage beim Abbrechen des Panels und für den
   * Festschreib-Knopf am Lese-Detail (Festschreiben friert den Serverstand ein, ein noch
   * nicht gespeichertes Feld wäre danach unwiederbringlich weg — und der Knopf ist so lange
   * gesperrt, samt dem Satz, der es sagt).
   */
  const ungespeichert = (() => {
    if (entwurf === null || !istEntwurf(r)) return false;
    // Der Kopfblock PARTEIEN zählt mit: Er meldet seinen Entwurf erst, sobald ihn jemand
    // angefasst hat — und genau dann gibt es etwas zu verlieren.
    if (parteien.beruehrt) return true;
    const gespeichert = entwurfAus(r);
    return (Object.keys(gespeichert) as (keyof Entwurf)[])
      .some((feld) => gespeichert[feld] !== entwurf[feld]);
  })();

  /* Der Wächter gegen das Schliessen des BROWSERS (Global Constraint „kein stiller
     Datenverlust"): Der Abbrechen-Wächter fängt nur den Weg über den Knopf und Escape;
     ein geschlossener Reiter ginge an ihm vorbei. */
  useBeforeUnload(ungespeichert);

  /*
   * HIER STAND `darfVerlassenWerden()` — die Rückfrage vor dem Sprung zwischen Original und
   * Korrektur und vor dem Anlegen einer Korrektur. Sie ist mit der Fixrunde 1 (07.09.2026)
   * ERSATZLOS entfallen, und zwar nicht aus Nachlässigkeit, sondern weil sie nicht mehr
   * auslösen KANN:
   *
   * Ungespeichert ist der Kopf nur, solange das Formular steht — Abbrechen setzt ihn auf
   * den Serverstand zurück, Speichern auf die Antwort. Und solange das Formular steht, sind
   * die Sprünge in der Bezugszeile und der Korrektur-Knopf GESPERRT (`bearbeitung`): Sie
   * liegen hinter dem Schleier des Panels. Eine Rückfrage, die nie erscheint, ist
   * kein Schutz, sondern eine Behauptung — und beim nächsten Lesen ein Rätsel.
   *
   * Wer den Riegel wiederhaben will, muss zuerst die Sperre der Bezugszeilen aufheben;
   * dieser Absatz sagt, warum beides zusammengehört. Den Verlust der Eingabe hütet seither
   * allein die Fußleiste des Panels (`abbrechen` → `darfAbbrechen`).
   */

  /** Die Werte für die Platzhalter-Vorschau — genau das, was diese Ansicht ohnehin hat.
   *  Was fehlt, erscheint gar nicht erst und bleibt als sichtbares Token stehen. */
  function vorschauWerte(): Record<string, string> {
    const werte: Record<string, string> = { "datum.heute": formatDatum(heute()) };
    if (p.kundeName !== undefined && p.kundeName !== "") werte["kunde.name"] = p.kundeName;
    const firma = companies.find((c) => c.id === r.companyId);
    if (firma !== undefined) werte["firma.name"] = firma.name;
    if (r.invoiceNumber !== null && r.invoiceNumber !== undefined) {
      werte["rechnung.nummer"] = r.invoiceNumber;
    }
    if (r.invoiceDate !== null && r.invoiceDate !== undefined) {
      werte["rechnung.datum"] = formatDatum(r.invoiceDate);
    }
    return werte;
  }

  /**
   * SPEICHERN im Panel — über den Formularzustand (`kopfFormular`). Gelingt es, steht der
   * frische Beleg in der Liste des Rahmens, und das Panel geht zu.
   *
   * SPEICHERN SCHLIESST DAS PANEL — es ist einer seiner beiden Ausgänge (Hausregel
   * „striktes Modalpanel", 06.09.2026), wie an Angebot und Auftrag auch. Es schliesst aber
   * NICHT den Eintrag: Dahinter steht das Lese-Detail mit den Ausgabewegen, dem
   * Ablagestand, dem Render-Modell und den Aktionen — und dort wartet auch „Festschreiben"
   * (Fund G2 bleibt damit geschlossen, nur einen Schritt ehrlicher: Was eingefroren wird,
   * ist der eben gespeicherte Stand).
   */
  async function kopfSpeichern(): Promise<void> {
    if (entwurf === null) return;
    const stand = await kopfFormular.aktion();
    if (stand === undefined) return;
    p.onFortschreiben(stand);
    setEntwurf(entwurfAus(stand));
    setBearbeitung(false);
    p.onMeldung({ text: "Der Rechnungskopf ist gespeichert.", art: "gut" });
  }

  /**
   * Der Schreibweg im Formularzustand: Kopf → Parteien. Wirft im Fehlerfall — eine 409 (auch
   * aus dem zweiten Weg) als SIE SELBST, damit der fremde Stand für die ganze Maske gilt.
   */
  async function kopfSchreiben(): Promise<Rechnung> {
    const e = entwurf ?? entwurfAus(r);
    let frisch: Rechnung;
    try {
      // `paymentTermText` geht bewusst NICHT mit: Der Satz ist der Snapshot der bei der
      // Ableitung aufgelösten Kondition, kein Feld dieser Maske. Weggelassen bleibt er
      // stehen (Teilsemantik des Vertrags) — mitgeschickt löschte ihn jede Speicherung.
      frisch = await client.updateInvoice(kundeId, r.id, r.version, {
        invoiceDate: oderNull(e.invoiceDate),
        servicePeriodStart: oderNull(e.servicePeriodStart),
        servicePeriodEnd: oderNull(e.servicePeriodEnd),
        introText: oderNull(e.introText),
        outroText: oderNull(e.outroText),
        // Der Lieferort ist VOLLERSATZ (anders als paymentTermText): Alle fünf Felder
        // gehen IMMER mit, ein geleertes Feld heisst wirklich „kein abweichender
        // Lieferort" — sonst liesse sich ein einmal gepflegter Ort nie wieder loswerden.
        deliveryName: oderNull(e.deliveryName),
        deliveryStreet: oderNull(e.deliveryStreet),
        deliveryPostalCode: oderNull(e.deliveryPostalCode),
        deliveryCity: oderNull(e.deliveryCity),
        deliveryCountry: oderNull(e.deliveryCountry),
        // invoiceFormat (Migration 025) ist VOLLERSATZ wie der Lieferort und das Datum —
        // der EDITIERTE Wert geht mit, nicht der Bestandswert vom Beleg.
        invoiceFormat:
          e.invoiceFormat as "PDF_A3" | "ZUGFERD_EN16931" | "XRECHNUNG_30",
      });
    } catch (fehler) {
      // Die einzige Quelle von Feldbefunden an dieser Maske ist der SERVER (422 mit
      // `fields[]`). Nennt er ein Feld im eingeklappten Kopfblock, geht der Block im SELBEN
      // Zug auf, in dem der Befund ans Feld kommt; den Fokus setzt danach der Sprung des
      // Formulars zum ersten Fehler — sonst stünde die Meldung über einem Feld, das niemand
      // sieht: die Fallenklasse „unsichtbarer Fehler".
      if (betrifftKopfblock(fehler)) kopf.oeffnen();
      throw fehler instanceof ApiError ? fehler : new Error(
        apiMeldung(fehler, "Der Rechnungskopf konnte nicht gespeichert werden."));
    }
    // Der zweite Speicherweg im selben Klick — Reihenfolge, Version und Sinn stehen an
    // `speichereParteien` (vorgangsdetail.ts), damit alle drei Beleg-Masken EINE Naht
    // haben.
    /*
     * Die Version NACH dem zweiten Weg. Sie ist hier nicht Kosmetik, sondern die
     * Bedingung dafür, dass es überhaupt weitergeht: Anders als Angebot und Auftrag
     * schliesst diese Maske ihren Eintrag NICHT und lädt die Liste NICHT neu — hinter
     * dem Panel steht das Lese-Detail, und dort wartet „Festschreiben". Mit der Version
     * aus dem Kopf-Speichern liefe genau dieser nächste Griff in eine 409 über die
     * eigene Änderung („Diese Rechnung wurde zwischenzeitlich geändert"), obwohl niemand
     * sonst etwas angefasst hat.
     */
    let parteienVersion: number | null = null;
    const folgefehler: { e: unknown } = { e: null };
    const parteienGespeichert = await speichereParteien({
      client, art: "invoice", customerId: r.customerId, vorgangId: r.id,
      version: frisch.version, griff: parteien,
      beiErfolg: (version) => { parteienVersion = version; },
      beiFehler: (meldung, fehler) => {
        // Der Kopf steht, die Parteien nicht: Der frische Beleg geht in die Liste des
        // Rahmens (sonst rechnete der zweite Anlauf mit der alten Version), das Panel
        // bleibt offen, und der Befund zeigt auf sein Feld.
        p.onFortschreiben(frisch);
        setEntwurf(entwurfAus(frisch));
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeRechnungsFehlerFeld(kopf, fehler);
      },
    });
    if (!parteienGespeichert) throw folgefehler.e;
    return parteienVersion === null ? frisch : { ...frisch, version: parteienVersion };
  }

  function festschreibenOeffnen(): void {
    p.onMeldung(null);
    aktionsMeldungRaeumen();
    kastenFormular.zuruecksetzen();
    // Vorgabe: das gesetzte Rechnungsdatum, sonst heute. Der Server nimmt auch gar keines
    // entgegen (dann setzt er den Berliner Kalendertag) — die Maske schlägt trotzdem einen
    // Wert vor, damit sichtbar ist, welches Datum der Beleg tragen wird.
    setFestschreibDatum(r.invoiceDate ?? heute());
    setFestschreibOffen(true);
  }

  /**
   * FESTSCHREIBEN — über den Formularzustand des Kastens (ME-Etappe 3, Aufgabe 5): Während
   * des Aufrufs ist das Datum gesperrt und beide Knöpfe zu; eine 409 lässt den Kasten mit
   * „Neu laden" stehen (vorher: Kasten zu, still nachgeladen), ein Fehlschlag steht IM
   * Kasten, an dem geklickt wurde.
   */
  async function festschreiben(): Promise<void> {
    // Die Version kommt aus dem hereingereichten Satz und wird beim Aufruf gelesen, nicht
    // beim Öffnen des Kastens: Zwischen beidem kann ein Positionsschritt gelegen haben.
    kastenWeg.current = () => mitRueckfall(
      () => client.issueInvoice(kundeId, r.id, r.version, oderNull(festschreibDatum)),
      "Die Rechnung konnte nicht festgeschrieben werden.");
    const frisch = await kastenFormular.aktion();
    if (frisch === undefined) return;
    p.onFortschreiben(frisch);
    setFestschreibOffen(false);
    // Ein festgeschriebener Beleg ist nicht mehr bearbeitbar: Steht er offen, wird die
    // Ansicht auf nur-lesend umgestellt statt eine tote Maske stehen zu lassen. Zeilen
    // und Ablagestand holt der Effekt oben nach, sobald der frische Status ankommt.
    // `setBearbeitung(false)` gehört dazu — sonst stünde über der frisch nummerierten
    // Rechnung weiter das Entwurfs-Panel.
    if (offen) {
      setBearbeitung(false);
      setEntwurf(null);
      setLadeFehler(null);
      kopfFormular.zuruecksetzen();
      setRenderModell(null);
      setRenderFehler(null);
      setEngineMeldung(null);
      setPositionen(null);
      setAblage(undefined);
      setDatev(undefined);
    }
    p.onMeldung({
      text: `Die Rechnung ist festgeschrieben — Nummer ${frisch.invoiceNumber ?? ""}.`,
      art: "gut",
    });
  }

  /**
   * **Das BEZAHLT-Kennzeichen** (Migration 018) — Kasten auf, Datum vorbelegt.
   *
   * Vorgabe ist der heutige Tag und nicht das Rechnungsdatum: Wer hier klickt, hat gerade
   * einen Zahlungseingang gesehen. Steht schon ein Datum, wird DAS vorgeschlagen — der
   * Knopf heisst dann „Bezahlt-Datum ändern" und soll den vorhandenen Wert zeigen, nicht
   * stillschweigend auf heute springen.
   */
  function bezahltOeffnen(): void {
    p.onMeldung(null);
    aktionsMeldungRaeumen();
    kastenFormular.zuruecksetzen();
    setBezahltDatum(r.paidAt ?? heute());
    setBezahltOffen(true);
  }

  async function alsBezahltMarkieren(): Promise<void> {
    // Die Version aus dem hereingereichten Satz, beim Aufruf gelesen — wie beim
    // Festschreiben.
    kastenWeg.current = () => mitRueckfall(
      () => client.markInvoicePaid(kundeId, r.id, r.version, oderNull(bezahltDatum)),
      "Die Rechnung konnte nicht als bezahlt markiert werden.");
    const frisch = await kastenFormular.aktion();
    if (frisch === undefined) return;
    p.onFortschreiben(frisch);
    setBezahltOffen(false);
    // KEIN Umschalten der Ansicht wie beim Festschreiben: Der Beleg bleibt, was er war
    // (ISSUED) — es ändert sich genau ein Feld, und der Kasten zeigt danach dasselbe
    // wie vorher, nur mit der Zeile „Bezahlt am …".
    p.onMeldung({
      text: `Die Rechnung ist als bezahlt markiert${
        frisch.paidAt === null ? "" : ` (${formatDatum(frisch.paidAt)})`}.`,
      art: "gut",
    });
  }

  /** Einen der beiden Kästen schliessen — ohne Aufruf, samt seinem Formularzustand. */
  function kastenSchliessen(): void {
    if (kastenFormular.laeuft) return;
    kastenFormular.zuruecksetzen();
    setFestschreibOffen(false);
    setBezahltOffen(false);
  }

  /**
   * Die RÜCKNAHME des Kennzeichens — mit Bestätigungsfrage (`useBestaetigung`, wie beim
   * Stornieren): Eine reine Ja/Nein-Frage braucht keinen eigenen Kasten.
   *
   * Gefragt wird, obwohl der Schritt umkehrbar ist: Er löscht eine gepflegte Angabe, und
   * ein versehentlicher Klick verlöre das Datum, das jemand aus dem Kontoauszug
   * herausgesucht hat.
   */
  async function bezahltZuruecknehmen(): Promise<void> {
    if (!(await bestaetigen({
      titel: "Bezahlt-Kennzeichen zurücknehmen?",
      text: `Das Bezahlt-Kennzeichen von Rechnung ${r.invoiceNumber ?? ""} wird `
        + "zurückgenommen. Die Rechnung steht danach wieder als offen in der Liste.",
      bestaetigenText: "Zurücknehmen",
    }))) {
      return;
    }
    p.onMeldung(null);
    aktionWeg.current = () => mitRueckfall(
      () => client.unmarkInvoicePaid(kundeId, r.id, r.version),
      "Das Bezahlt-Kennzeichen konnte nicht zurückgenommen werden.");
    const frisch = await aktionFormular.aktion();
    if (frisch === undefined) return;
    p.onFortschreiben(frisch);
    p.onMeldung({ text: "Das Bezahlt-Kennzeichen ist zurückgenommen.", art: "gut" });
  }

  async function stornieren(): Promise<void> {
    // Rückfrage, weil es kein Zurück gibt: Eine stornierte Rechnung bleibt storniert, Nummer
    // und Beträge bleiben stehen. Als Bestätigungsfrage (striktes Modal, Aufgabe 8) — eine
    // reine Ja/Nein-Frage braucht keinen eigenen Kasten (anders als das Festschreiben, das
    // ein Datum aufnimmt).
    if (!(await bestaetigen({
      titel: `Rechnung ${r.invoiceNumber ?? ""} stornieren?`,
      text: `Rechnung ${r.invoiceNumber ?? ""} wirklich stornieren? Sie bleibt mit ihrer `
        + "Nummer stehen und lässt sich danach nicht mehr ändern.",
      bestaetigenText: "Stornieren",
      gefaehrlich: true,
    }))) {
      return;
    }
    p.onMeldung(null);
    aktionWeg.current = () => mitRueckfall(
      () => client.cancelInvoice(kundeId, r.id, r.version),
      "Die Rechnung konnte nicht storniert werden.");
    const frisch = await aktionFormular.aktion();
    if (frisch === undefined) return;
    p.onFortschreiben(frisch);
    // KEIN Umschalten des geöffneten Kastens wie beim Festschreiben: „Stornieren" steht
    // ausschliesslich im geschlossenen Eintrag und ist dort gesperrt, solange überhaupt
    // eine Rechnung offen ist. Es kann hier also nichts offen sein.
    p.onMeldung({ text: "Die Rechnung ist storniert.", art: "gut" });
  }

  /**
   * **„Korrekturrechnung erstellen"** — der Knopf an der STORNIERTEN Rechnung.
   *
   * Er legt einen Rechnungs-ENTWURF an, der dieselben Positionen mit umgekehrtem
   * Vorzeichen trägt, und schlägt ihn gleich auf: Dieselbe Naht wie „Rechnung erzeugen" am
   * Auftrag — wer eine Handlung auslöst, will das Ergebnis sehen und nicht in einer Liste
   * danach suchen.
   *
   * Gab es die Korrektur SCHON, entsteht keine zweite (die API antwortet dann mit 200 und
   * genau dieser Korrektur). Die Maske sagt das ausdrücklich, statt so zu tun, als sei eben
   * etwas Neues entstanden.
   *
   * Das Neuladen der Liste und das Aufschlagen macht der RAHMEN (`onKorrekturAngelegt`):
   * Es haben sich ZWEI Sätze geändert — die Korrektur ist dazugekommen, und das Original
   * trägt nun den Rückverweis, an dem sein Knopf hängt —, und wohin „aufschlagen" führt,
   * weiss nur er (andere Zeile der Liste bzw. `/rechnungen/<id>`).
   */
  async function korrekturErstellen(): Promise<void> {
    // Rückfrage wie vor dem Stornieren, und aus demselben Grund: Der Klick ist
    // unwiderruflich. Eine einmal angelegte Korrektur lässt sich NICHT mehr entfernen —
    // für Rechnungen gibt es in dieser Anwendung überhaupt keinen Löschweg —, und solange
    // sie steht, legt der Knopf auch keine zweite an.
    if (!(await bestaetigen({
      titel: "Korrekturrechnung anlegen?",
      text: `Zu Rechnung ${r.invoiceNumber ?? ""} eine Korrekturrechnung anlegen? Sie `
        + "entsteht als Entwurf mit umgekehrten Vorzeichen und lässt sich danach nicht mehr "
        + "entfernen.",
      bestaetigenText: "Korrektur anlegen",
      // KEINE Gefahr-Frage (Gesamtreview Etappe 4, H8): unumkehrbar, aber ohne Datenverlust —
      // die Hausregel färbt nur Fragen rot, nach denen etwas weg ist.
    }))) {
      return;
    }
    setBeschaeftigt(true);
    p.onMeldung(null);
    aktionsMeldungRaeumen();
    try {
      const { korrektur, bestand } = await client.createInvoiceKorrektur(kundeId, r.id);
      await p.onKorrekturAngelegt(korrektur, bestand
        ? "Zu dieser Rechnung gibt es bereits eine Korrektur — sie ist hier aufgeschlagen. "
          + "Eine zweite entsteht nicht."
        : "Die Korrekturrechnung ist angelegt: dieselben Positionen mit umgekehrtem "
          + "Vorzeichen, die Texte übernommen. Sie ist noch ein Entwurf.");
    } catch (e) {
      p.onMeldung({
        text: apiMeldung(e, "Die Korrekturrechnung konnte nicht angelegt werden."),
        art: "schlecht",
      });
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * Der EINE Ausgang des Bearbeitungspanels neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei
   * ungespeicherten Kopfeingaben. Knopf und Escape gehen durch diese Funktion.
   *
   * Verglichen wird gegen den Beleg, wie er GERADE steht (`ungespeichert` misst genau das) —
   * nicht gegen einen eingefrorenen Ausgangsstand: Ein Schritt im Positions-Editor dreht die
   * Version weiter, und der Beleg kommt über `onFortschreiben` frisch wieder herein.
   *
   * Die POSITIONEN selbst stehen nie ungespeichert im Panel — der Editor schreibt jeden
   * Schritt sofort. Die Rückfrage kann sie deshalb übergehen, ohne etwas zu verschweigen
   * (dieselbe Begründung wie an AngebotDetail).
   */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(ungespeichert))) return;
    parteien.zuruecksetzen();
    // Zurück auf den Serverstand: Sonst hinge der verworfene Entwurf weiter in `entwurf`,
    // und das Lese-Detail dahinter hielte seinen „Festschreiben"-Knopf für gesperrt
    // (`ungespeichert`) — für Eingaben, die es gar nicht mehr gibt.
    setEntwurf(istEntwurf(r) ? entwurfAus(r) : null);
    kopfFormular.zuruecksetzen();
    setBearbeitung(false);
  }

  /** Der Sprung zum anderen Beleg — der Baustein führt ihn nicht aus, er meldet ihn (siehe
   *  `onSpringeZuRechnung`). Ohne Rückfrage: Es gibt nichts zu verlieren, solange er
   *  überhaupt anklickbar ist (siehe den Absatz zu `darfVerlassenWerden` oben). */
  function springeZu(id: string): void {
    p.onSpringeZuRechnung(id);
  }

  /** Das Render-Modell wird erst beim Aufklappen geholt — es ist ein Arbeitsstand, kein
   *  Teil der Maske, und niemand soll dafür bei jedem Öffnen einer Rechnung warten. */
  async function renderModellLaden(): Promise<void> {
    if (renderModell !== null) return;
    setRenderFehler(null);
    try {
      setRenderModell(await client.invoiceRenderModel(kundeId, r.id));
    } catch (e) {
      setRenderFehler(apiMeldung(e, "Das Render-Modell konnte nicht geladen werden."));
    }
  }

  async function rendern(profil: Ausgabeprofil): Promise<void> {
    const weg = AUSGABEWEGE[profil];
    setEngineMeldung(null);
    try {
      // Seit E3-T4 der echte Weg: Die Engine setzt den Beleg, die API liefert ihn als
      // Download durch (attachment + nosniff + CSP), hier wird er gespeichert. Das PROFIL
      // entscheidet, was ankommt — PDF, hybrides PDF oder XML.
      const { blob, filename } = await client.renderInvoice(kundeId, r.id, profil);
      const name = filename ?? weg.rueckfallName;
      speichereBlob(blob, name);
      setEngineMeldung({
        text: `${weg.ergebnis} wurde heruntergeladen (${name}).`, art: "gut",
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === "PFLICHTWERTE_FEHLEN") {
        // Der Fall, für den die E-Knöpfe gebaut sind: Die Engine sagt, WELCHE Pflichtwerte
        // des Profils fehlen (BR-DE-5/6/7 verlangen den Verkäufer-Kontakt). Ihre Feldpfade
        // kommen ausschliesslich strukturiert (`fields[]`) und werden unten als Liste
        // gezeigt; die MELDUNG sagt nur, dass es klemmt — und die Maske ergänzt, wo man
        // die Lücke schliesst.
        setEngineMeldung({
          text: apiMeldung(e, "Für dieses Profil fehlen Pflichtwerte.")
            + PFLICHTWERTE_HINWEIS,
          art: "schlecht",
          // Der Schlüssel entfällt, wenn keine kamen (die API lässt `fields` dann ganz
          // weg) — `exactOptionalPropertyTypes`.
          ...(e.fields === undefined ? {} : { felder: e.fields }),
        });
      } else if (e instanceof ApiError && e.status === 501) {
        // KEIN Fehlschlag, sondern der bekannte Arbeitsstand: neutral gezeichnet und als
        // Status angesagt.
        setEngineMeldung({
          // „Beleg" statt „PDF": Dieser Satz steht seit den E-Rechnungs-Knöpfen auch
          // hinter „Als XRechnung", und dort entstünde kein PDF, sondern XML.
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein "
            + "Beleg. Das Render-Modell unten zeigt, was sie bekommen wird.",
          art: "hinweis",
        });
      } else {
        // Jeder ANDERE Ausgang ist ein echter Fehlschlag — Fehlerfarbe und Alarm. Nennt die
        // Gegenstelle dabei Feldpfade (die API hängt sie seit dem 05.09.2026 an JEDEN
        // Engine-Fehlschlag, nicht nur an die 422), stehen sie auch hier als Liste — sonst
        // gingen sie verloren, seit die Meldung sie nicht mehr aufzählt.
        setEngineMeldung({
          text: apiMeldung(e, "Die Rechnung konnte nicht gerendert werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    }
  }

  /**
   * „PDF in Akte ablegen" — das Nachholen und Erneuern von Hand.
   *
   * Beim Festschreiben läuft derselbe Schritt am Server automatisch; klemmte dort die
   * Engine, steht die Rechnung trotzdem fest (das ist Absicht) und dieser Knopf holt die
   * Ablage nach. Er ist IDEMPOTENT: Liegt die Datei schon, wird ihr Inhalt ersetzt — es
   * entsteht kein zweiter Eintrag in der Akte.
   *
   * Die Rückmeldungen gehen in dieselbe `engineMeldung` wie die der Render-Knöpfe: Es ist
   * derselbe Kasten, dieselbe Gegenstelle und dieselben drei Ausgänge.
   */
  async function inAkteAblegen(): Promise<void> {
    setEngineMeldung(null);
    aktionsMeldungRaeumen();
    setBeschaeftigt(true);
    try {
      const stand = await client.createInvoiceAblage(kundeId, r.id);
      setAblage(stand);
      setEngineMeldung({
        text: `Das PDF liegt jetzt in der Akte (${stand.fileName}) — im Reiter „Dateien".`,
        art: "gut",
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setEngineMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein "
            + "Beleg, der abgelegt werden könnte.",
          art: "hinweis",
        });
      } else {
        setEngineMeldung({
          text: apiMeldung(e, "Das PDF konnte nicht in der Akte abgelegt werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  const kuerzel = companies.find((c) => c.id === r.companyId)?.shortCode ?? null;
  // Die Korrektur ZU dieser Rechnung, falls es eine gibt (Migration 017). Als eigene
  // Konstante und nicht als `r.correctedByInvoiceId` im JSX: Innerhalb eines Rückrufs
  // verliert TypeScript die Verengung einer EIGENSCHAFT wieder, und aus dem Sprung würde
  // ein `!`.
  const korrekturId = alsBezug(r.correctedByInvoiceId);

  return (
    <>
      <div className="krg__zeile">
        <span className="krg__nummer">{r.invoiceNumber ?? "Entwurf"}</span>
        {kuerzel !== null && <span className="krg__kuerzel">{kuerzel}</span>}
        <span className="krg__auftrag">zu Auftrag {r.salesOrderNumber}</span>
        <span className={`krg__status krg__status--${r.status.toLowerCase()}`}>
          {RECHNUNG_STATUS_LABEL[r.status]}
        </span>
      </div>

      <div className="krg__daten">
        {/* BRUTTO, nicht netto (Spec: „Liste (Nummer/Entwurf, Datum, Betrag brutto,
            Status)"; Dirk, 03.09.: „Brutto nachrüsten"). Die Liste ist die Stelle, an der
            jemand den Rechnungsbetrag SUCHT — und was eine Rechnung kostet, ist ihr
            Bruttobetrag. Gerechnet hat ihn der SERVER aus den Positionen (Umsatzsteuer je
            Steuerfall); hier wird nichts gebildet, nur deutsch formatiert. */}
        <span className="krg__betrag ki-num">
          {formatBetrag(r.grossAmount, r.currency)} brutto
        </span>
        <span className="krg__datum">
          {r.invoiceDate === null ? "ohne Rechnungsdatum" : formatDatum(r.invoiceDate)}
        </span>
        {/*
          Das BEZAHLT-Kennzeichen (Migration 018) steht als EIGENE Angabe neben dem Status
          und nicht IN ihm: Der Status sagt, was mit dem Beleg ist (festgeschrieben,
          storniert), diese Zeile, was mit dem Geld ist. Beides in eine Marke zu falten
          hiesse, an einer stornierten und bezahlten Rechnung eine der beiden Auskünfte zu
          verlieren — und genau die wird gebraucht, wenn jemand einer Zahlung nachgeht.
        */}
        {r.paidAt !== null && (
          <span className="krg__bezahlt">Bezahlt am {formatDatum(r.paidAt)}</span>
        )}
      </div>

      {/* Ein Kasten gilt nur, solange sein Beleg dazu passt: Hat „Neu laden" im fremden
          Stand einen Beleg hereingebracht, den ein anderer schon festgeschrieben (bzw.
          storniert) hat, fällt der Kasten weg, statt eine Handlung anzubieten, die der
          Server abweist. */}
      {festschreibOffen && istEntwurf(r)
        ? festschreibDialog()
        : bezahltOffen && r.status === "ISSUED"
          ? bezahltDialog()
          : offen
          ? offenerKasten()
          : (
            <>
            <div className="krg__aktionen">
              <KiKnopf
                // „Ansehen" auch am ENTWURF (Fixrunde 1, 07.09.2026): Der Knopf schlägt
                // das Lese-Detail auf, nicht das Formular — das öffnet erst „Bearbeiten"
                // darin. Ein Knopf, der „Bearbeiten" verspricht und ein Detail liefert,
                // wäre eine Beschriftung, die ihre Handlung nicht nennt.
                name={istEntwurf(r)
                  ? `Rechnung zu Auftrag ${r.salesOrderNumber} ansehen`
                  : `Rechnung ${r.invoiceNumber ?? ""} ansehen`}
                deaktiviert={gesperrt || belegt} onClick={p.onOeffnen}>
                Ansehen
              </KiKnopf>
              {istEntwurf(r) && (
                <KiKnopf art="primaer" deaktiviert={gesperrt || belegt}
                  onClick={festschreibenOeffnen}>
                  Festschreiben
                </KiKnopf>
              )}
              {/* Das BEZAHLT-Kennzeichen (Migration 018) — nur an der festgeschriebenen
                  Rechnung, und dort genau EIN Knopf je Zustand: solange nichts steht der
                  Markier-Knopf, danach der Weg zum Ändern des Datums. Die RÜCKNAHME steht
                  daneben und auch an der stornierten Rechnung (unten): Ein Fehlgriff soll
                  sich richtigstellen lassen, statt für immer am Beleg zu kleben. */}
              {r.status === "ISSUED" && (
                <KiKnopf deaktiviert={gesperrt || belegt} onClick={bezahltOeffnen}>
                  {r.paidAt === null ? "Als bezahlt markieren" : "Bezahlt-Datum ändern"}
                </KiKnopf>
              )}
              {r.paidAt !== null && (
                <KiKnopf deaktiviert={gesperrt || belegt}
                  onClick={() => { void bezahltZuruecknehmen(); }}>
                  Bezahlt-Kennzeichen zurücknehmen
                </KiKnopf>
              )}
              {/* Stornieren gibt es NUR an einer festgeschriebenen Rechnung: Ein Entwurf
                  war nie in der Welt (der Server antwortet dort 422). Eine BEZAHLTE
                  Rechnung lässt sich weiterhin stornieren — das Kennzeichen ändert an den
                  Belegregeln nichts. */}
              {r.status === "ISSUED" && (
                <KiKnopf deaktiviert={gesperrt || belegt}
                  onClick={() => { void stornieren(); }}>
                  Stornieren
                </KiKnopf>
              )}
              {/* Die KORREKTUR (Migration 017) — nur an der stornierten Rechnung, und dort
                  genau EIN Knopf: Solange es keine Korrektur gibt, der Erstellen-Knopf;
                  sobald es eine gibt, der Sprung zu ihr. Beides nebeneinander hiesse, einen
                  Knopf anzubieten, der nur noch dorthin führt, wo der andere schon
                  hinführt. */}
              {r.status === "CANCELLED" && korrekturId === null && (
                <KiKnopf art="primaer" deaktiviert={gesperrt || belegt}
                  onClick={() => { void korrekturErstellen(); }}>
                  {beschaeftigt ? "Korrekturrechnung erstellen …"
                                : "Korrekturrechnung erstellen"}
                </KiKnopf>
              )}
              {r.status === "CANCELLED" && korrekturId !== null && (
                <KiKnopf deaktiviert={gesperrt || belegt}
                  onClick={() => springeZu(korrekturId)}>
                  Zur Korrektur
                </KiKnopf>
              )}
            </div>
            {aktionsHinweise()}
            </>
          )}
      {bestaetigung}
    </>
  );

  /**
   * Was die Aktionen OHNE Feld (Stornieren, Bezahlt-Kennzeichen zurücknehmen) zurückmelden,
   * wenn es nicht gelungen ist — am Beleg, unter den Knöpfen. Schlechtestes oben: der fremde
   * Stand (er sperrt die Aktionen) vor dem Fehlschlag. Erfolge meldet weiterhin der Rahmen.
   */
  function aktionsHinweise(): ReactNode {
    return (
      <>
        {/* Hausregel „Schlechtestes oben“: der Fehler (z. B. gescheitertes Neu laden) steht
            über der Warnung zum fremden Stand (Gesamtreview Etappe 3). */}
        {aktionFormular.meldung !== null && (
          <Hinweis art="fehler" className="krg__aktionshinweis">
            {aktionFormular.meldung}
          </Hinweis>
        )}
        {aktionFormular.zustand === "fremder-stand" && (
          <Hinweis art="warnung" className="krg__aktionshinweis"
            aktionen={(
              <KiKnopf onClick={() => { void aktionFormular.neuLaden(); }}>
                Neu laden
              </KiKnopf>
            )}>
            Diese Rechnung wurde inzwischen geändert. „Neu laden“ holt den aktuellen Stand —
            danach die Aktion bitte erneut wählen.
          </Hinweis>
        )}
      </>
    );
  }

  /** Der Text der Hinweiszeile „fremder Stand" in einem Formular dieses Belegs (Slot
   *  `hinweis` von `Formular`) — geduzt wie die übrige Oberfläche, statt des Vorgabetextes
   *  des Elements (Befund 11 an me-design). */
  function fremderStandHinweis(
    zustand: { zustand: string; fremderStand: { changedBy: string } | null },
    weiter: string,
  ): ReactNode {
    if (zustand.zustand !== "fremder-stand") return undefined;
    const wer = zustand.fremderStand?.changedBy;
    return (
      <>
        {wer !== undefined && wer !== ""
          ? `${wer} hat diese Rechnung inzwischen geändert.`
          : "Diese Rechnung wurde inzwischen geändert."}
        {` ${weiter}`}
      </>
    );
  }

  /**
   * Der Festschreib-Dialog. Ein eigener Kasten und KEINE blosse Bestätigungsfrage, weil er
   * ein FELD trägt (das Rechnungsdatum, vorbelegt mit heute) — und weil die beiden Folgen
   * ausdrücklich dastehen sollen: Die Rechnung zieht ihre Nummer, und danach ist sie eingefroren.
   *
   * `role="group"` mit `aria-label`: Kein modaler Dialog, der den Rest der Seite sperrt —
   * der Kasten steht an der Rechnung, um die es geht.
   */
  function festschreibDialog(): ReactElement {
    const kf = kastenFormular;
    return (
      <div className="krg__dialog" role="group" aria-label="Rechnung festschreiben">
        <p className="krg__dialog-text">
          Beim Festschreiben zieht die Rechnung ihre <strong>Rechnungsnummer</strong> aus dem
          Nummernkreis des Unternehmens — lückenlos und nur einmal. Danach ist sie
          <strong> eingefroren</strong>: Kopf und Positionen nehmen keine Änderung mehr an,
          rückgängig geht nur noch ein Storno.
        </p>
        {/* Das Feld im Raster des Formulars (ME-Etappe 3, Aufgabe 5): gesperrt während des
            Aufrufs und im fremden Stand, Befund am Feld, Hinweiszeile mit „Neu laden". */}
        <Formular beschriftung="oben" {...kf.formularProps}
          hinweis={fremderStandHinweis(kf,
            "„Neu laden“ holt den aktuellen Stand — danach bitte erneut festschreiben.")}>
          <Feld id={`${feldId}-festschreib-datum`} label="Rechnungsdatum" typ="date"
            bearbeitbar wert={festschreibDatum} onChange={setFestschreibDatum}
            hilfe="Leer gelassen setzt der Server den heutigen Tag."
            {...feldFehlerProp(kf.feldFehler, "invoiceDate")} />
        </Formular>
        {/* Was der Klick sonst noch tut, BEVOR er es tut: Die Anwendung legt das PDF im
            Anschluss in der Akte ab — das kostet einen Augenblick, und wer das weiss,
            hält den kurz stehenden Knopf nicht für eine klemmende Maske. */}
        <p className="krg__hinweis krg__hinweis--klein">
          Im Anschluss wird das Beleg-PDF erzeugt und in der Akte abgelegt; das dauert
          einen Moment. Klappt es nicht, bleibt die Rechnung trotzdem festgeschrieben —
          das PDF lässt sich danach mit einem Klick nachholen.
        </p>
        {kf.meldung !== null && <Hinweis art="fehler">{kf.meldung}</Hinweis>}
        <div className="krg__formular-aktionen">
          <KiKnopf art="primaer" deaktiviert={kf.zustand !== "bereit"}
            onClick={() => { void festschreiben(); }}>
            {/* Die Beschriftung SPRICHT während des Laufs: Der Knopf ist gesperrt, solange
                die Festschreibung samt Beleg-Ablage läuft — ohne diesen Wechsel sähe das
                nach „hat mein Klick gezählt?" aus. */}
            {kf.laeuft ? "Festschreiben …" : "Festschreiben"}
          </KiKnopf>
          <KiKnopf deaktiviert={kf.laeuft} onClick={kastenSchliessen}>
            Abbrechen
          </KiKnopf>
        </div>
      </div>
    );
  }

  /**
   * Der BEZAHLT-Kasten (Migration 018). Ein eigener Kasten und keine blosse
   * Bestätigungsfrage — aus demselben Grund wie beim Festschreiben: Er trägt ein FELD, den
   * Zahlungstag.
   *
   * Und er sagt ausdrücklich, was NICHT geschieht: Der Beleg ändert sich nicht. Ohne diesen
   * Satz liest sich „Als bezahlt markieren" wie ein Statuswechsel, und wer schon einmal
   * eine Rechnung festgeschrieben hat, klickt hier vorsichtiger, als er müsste.
   *
   * `role="group"` mit `aria-label`: Kein modaler Dialog — der Kasten steht an der
   * Rechnung, um die es geht.
   */
  function bezahltDialog(): ReactElement {
    const kf = kastenFormular;
    return (
      <div className="krg__dialog" role="group" aria-label="Rechnung als bezahlt markieren">
        <p className="krg__dialog-text">
          Die Rechnung bekommt ein <strong>Bezahlt-Datum</strong>. Am Beleg ändert sich
          dadurch nichts — Nummer, Beträge und Status bleiben, wie sie sind; stornieren
          lässt sie sich weiterhin. Das Kennzeichen ist jederzeit
          <strong> zurücknehmbar</strong>.
        </p>
        <Formular beschriftung="oben" {...kf.formularProps}
          hinweis={fremderStandHinweis(kf,
            "„Neu laden“ holt den aktuellen Stand — danach bitte erneut speichern.")}>
          <Feld id={`${feldId}-bezahlt-datum`} label="Bezahlt am" typ="date"
            bearbeitbar wert={bezahltDatum} onChange={setBezahltDatum}
            hilfe="Leer gelassen setzt der Server den heutigen Tag."
            {...feldFehlerProp(kf.feldFehler, "paidAt")} />
        </Formular>
        {kf.meldung !== null && <Hinweis art="fehler">{kf.meldung}</Hinweis>}
        <div className="krg__formular-aktionen">
          <KiKnopf art="primaer" deaktiviert={kf.zustand !== "bereit"}
            onClick={() => { void alsBezahltMarkieren(); }}>
            {kf.laeuft ? "Speichern …" : "Als bezahlt markieren"}
          </KiKnopf>
          <KiKnopf deaktiviert={kf.laeuft} onClick={kastenSchliessen}>
            Abbrechen
          </KiKnopf>
        </div>
      </div>
    );
  }

  /**
   * Der aufgeschlagene Beleg — das LESE-DETAIL, und über einem Entwurf gegebenenfalls das
   * Bearbeiten-Panel.
   *
   * Die Teilung ist der Entscheid Dirks vom 06.09.2026: NUR das Bearbeiten wandert ins
   * strikte Modalpanel. Festschreiben, Bezahlt-Kennzeichen und Korrektur sind keine
   * Bearbeitung — sie bleiben Aktionen am Lese-Detail, samt ihren bestehenden
   * Rückfrage-Kästen. Ebenso die Ausgabewege, der Ablagestand und das Render-Modell: Sie
   * gehören zum Beleg, nicht zu seinem Formular.
   *
   * Deshalb steht dieses Detail auch dann, wenn das Panel darüber liegt — und vor allem,
   * wenn es geschlossen wird: Speichern und Abbrechen führen zurück HIERHER und nicht in
   * die blosse Liste. Genau das hält Fund G2 geschlossen („aus dem geöffneten Entwurf
   * führte kein Weg zum Festschreiben"), ohne dafür einen dritten Ausgang ins Panel zu
   * stellen.
   *
   * Solange das Panel steht, sind die Aktionen des Detail-Kastens GESPERRT (`bearbeitung`) — sie
   * liegen hinter dem Schleier und wären sonst Knöpfe, die niemand drücken kann. Der
   * Riegel ist damit kein toter Prop, sondern die ehrliche Auskunft: Wer bearbeitet, tut
   * gerade nichts anderes.
   */
  function offenerKasten(): ReactElement {
    return (
      <div className="krg__bearbeitung">
        {korrekturHinweis()}
        {!istEntwurf(r) && belegKasten()}
        {renderModellBlock()}
        <div className="krg__formular-aktionen">
          {/* Zurück ins Formular — der Weg, der nach Speichern oder Abbrechen offenbleibt.
              Ohne ihn wäre ein einmal geschlossenes Panel nur über das Zuklappen und
              erneute Aufschlagen des Eintrags wieder zu erreichen. */}
          {istEntwurf(r) && (
            <KiKnopf deaktiviert={bearbeitung || belegt}
              onClick={() => {
                // Der Kopfblock beginnt bei JEDEM Aufschlagen im Startzustand — nicht so,
                // wie ihn der vorige Anlauf hinterlassen hat. Der Entwurf der Parteien und
                // der Formularzustand gehören dazu: Kein Zustand überlebt das Schliessen
                // des Panels.
                kopf.zuruecksetzen();
                parteien.zuruecksetzen();
                kopfFormular.zuruecksetzen();
                // Ein stehender Fehlschlag einer Aktion ohne Feld gehört zum vorigen Schritt
                // (Re-Review A5): Wer bearbeitet, beginnt etwas Neues am Beleg.
                aktionsMeldungRaeumen();
                setBearbeitung(true);
              }}>
              Bearbeiten
            </KiKnopf>
          )}
          {/* Festschreiben AUCH von hier aus (Fund G2): Vorher führte aus dem geöffneten
              Entwurf kein Weg dorthin — man musste „Abbrechen" drücken, was nach Verwerfen
              klingt, obwohl die Positionen längst gespeichert sind. Gesperrt, solange der
              Kopf ungespeicherte Änderungen trägt: Was jetzt eingefroren wird, ist der
              Serverstand, nicht das Getippte. */}
          {istEntwurf(r) && (
            <KiKnopf deaktiviert={bearbeitung || speichert || ungespeichert}
              onClick={festschreibenOeffnen}>
              Festschreiben
            </KiKnopf>
          )}
          {/* Das BEZAHLT-Kennzeichen AUCH von hier aus: Aus dem aufgeschlagenen Beleg führte
              sonst kein Weg dorthin. Genau hier liest man ihn ja — wer den Beleg offen hat,
              um eine Zahlung zuzuordnen, soll sie nicht erst nach einem „Schliessen"
              quittieren können. */}
          {r.status === "ISSUED" && (
            <KiKnopf deaktiviert={bearbeitung || belegt} onClick={bezahltOeffnen}>
              {r.paidAt === null ? "Als bezahlt markieren" : "Bezahlt-Datum ändern"}
            </KiKnopf>
          )}
          {r.paidAt !== null && (
            <KiKnopf deaktiviert={bearbeitung || belegt}
              onClick={() => { void bezahltZuruecknehmen(); }}>
              Bezahlt-Kennzeichen zurücknehmen
            </KiKnopf>
          )}
          {/* „Korrekturrechnung erstellen" AUCH von hier aus — dieselbe Überlegung: Aus der
              aufgeschlagenen stornierten Rechnung führte sonst kein Weg dorthin, man musste
              erst „Schliessen" drücken und den Eintrag in der Liste wiederfinden. Nur,
              solange es noch keine Korrektur gibt — sonst steht der Sprung ohnehin oben in
              der Bezugszeile. */}
          {r.status === "CANCELLED" && korrekturId === null && (
            <KiKnopf deaktiviert={bearbeitung || belegt}
              onClick={() => { void korrekturErstellen(); }}>
              {beschaeftigt ? "Korrekturrechnung erstellen …"
                            : "Korrekturrechnung erstellen"}
            </KiKnopf>
          )}
          {/* „Schliessen" und nicht mehr „Abbrechen" am Entwurf: Abbrechen heisst seit der
              Hausregel überall dasselbe (die Eingabe verwerfen) und steht in der Fußleiste
              des Panels. Hier wird nichts verworfen, hier wird zugeklappt. */}
          <KiKnopf deaktiviert={bearbeitung} onClick={p.onSchliessen}>
            Schliessen
          </KiKnopf>
        </div>
        {/* Schlechtestes oben (Hausregel): Fremder Stand und Fehlschläge der Aktionen, dann
            der Ladefehler der Zeilen, zuletzt die blosse Auskunft zum Festschreiben. */}
        {aktionsHinweise()}
        {/* Der Fehler des LADENS der Zeilen (siehe `zeilenLesen`) — die Befunde des
            Speicherns stehen im Panel, bei den Feldern, die sie ausgelöst haben. */}
        {ladeFehler !== null && !bearbeitung && <Hinweis art="fehler">{ladeFehler}</Hinweis>}
        {istEntwurf(r) && ungespeichert && !bearbeitung && (
          <Hinweis art="info">
            Zum Festschreiben bitte zuerst speichern — eingefroren wird der gespeicherte
            Stand.
          </Hinweis>
        )}

        {bearbeitung && (
          <Modal titel={`Rechnung zu Auftrag ${r.salesOrderNumber} bearbeiten`} breite="weit"
            onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
            {entwurfsKasten()}
            {/* Die zwei einzigen Ausgänge, unten am Panel: Speichern schreibt die
                KOPFFELDER (die Positionen schreibt der Editor Schritt für Schritt
                selbst). Im fremden Stand bleibt Speichern zu, bis „Neu laden" den
                Serverstand geholt hat. */}
            <ModalFuss speichernText="Speichern"
              speichernGesperrt={kopfFormular.zustand !== "bereit"}
              abbrechenGesperrt={speichert}
              onSpeichern={() => { void kopfSpeichern(); }} onAbbrechen={abbrechen} />
          </Modal>
        )}
      </div>
    );
  }

  /**
   * **Der Korrektur-Bezug im aufgeschlagenen Kasten** (Migration 017) — in BEIDE
   * Richtungen, mit dem Sprung zum jeweils anderen Beleg.
   *
   * An der KORREKTUR: „Korrektur zur Rechnung <Nr> vom <Datum>". An der korrigierten
   * Rechnung umgekehrt: „Korrigiert durch <Nr>" — bzw. „Korrigiert durch Entwurf",
   * solange die Korrektur noch keine Nummer gezogen hat. Genau dieser Unterschied ist der
   * Grund, warum die Lese-Gestalt Kennung UND Nummer der Rückrichtung führt: „Entwurf" ist
   * eine andere Auskunft als „es gibt keine".
   *
   * Er steht am ENTWURF wie am festgeschriebenen Beleg (`offenerKasten` zeichnet ihn vor
   * beiden Fassungen): Der Bezug ist keine Eigenschaft des Bearbeitungszustands, sondern
   * des Belegs — und wer eine Rechnung mit negativen Beträgen aufschlägt, soll auf den
   * ersten Blick lesen, warum sie negativ ist.
   *
   * **BEIDE Zeilen, wenn beide Bezüge stehen.** Korrektur-KETTEN sind ausdrücklich erlaubt:
   * Wird eine festgeschriebene Korrektur selbst storniert, lässt sich zu ihr wieder eine
   * anlegen. Das mittlere Glied einer solchen Kette korrigiert etwas UND ist korrigiert
   * worden — zeigte die Maske nur den ersten Bezug, verschwiege sie am mittleren Beleg
   * genau die Auskunft, dass er nicht mehr der letzte Stand ist.
   *
   * `null`, wenn es keinen Bezug gibt: Eine Zeile „Keine Korrektur" an jeder gewöhnlichen
   * Rechnung wäre Rauschen (dieselbe Regel wie beim Lieferort).
   */
  function korrekturHinweis(): ReactElement | null {
    // Die Kennung des Originals als Konstante — innerhalb des Rückrufs unten verlöre
    // TypeScript die Verengung der Eigenschaft wieder.
    const originalId = alsBezug(r.correctsInvoiceId);
    if (originalId === null && korrekturId === null) return null;
    const datum = alsBezug(r.correctsInvoiceDate);
    return (
      <>
        {originalId !== null && (
          <Hinweis art="info" className="krg__korrektur">
            Korrektur zur Rechnung{" "}
            <strong>{r.correctsInvoiceNumber ?? "ohne Nummer"}</strong>
            {datum === null ? "" : ` vom ${formatDatum(datum)}`}.{" "}
            <KiKnopf art="leise" deaktiviert={bearbeitung}
              onClick={() => springeZu(originalId)}>
              Zum Original
            </KiKnopf>
          </Hinweis>
        )}
        {korrekturId !== null && (
          <Hinweis art="info" className="krg__korrektur">
            Korrigiert durch <strong>{r.correctedByInvoiceNumber ?? "Entwurf"}</strong>.{" "}
            <KiKnopf art="leise" deaktiviert={bearbeitung}
              onClick={() => springeZu(korrekturId)}>
              Zur Korrektur
            </KiKnopf>
          </Hinweis>
        )}
      </>
    );
  }

  /**
   * Das Formular eines ENTWURFS. Bewusst eine Funktion, die JSX zurückgibt, und keine
   * eigene Komponente: Eine im Render definierte Komponente bekäme bei jedem Neuzeichnen eine
   * neue Identität — React hängte ihren Teilbaum ab und neu auf, und der Fokus spränge nach
   * jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner/KundeAngebote).
   *
   * **Die Anordnung** ist seit dem 11.09.2026 dieselbe wie an Angebot und Auftrag (Paket
   * „Positionen mit Präsenz", Entscheid Dirk 08.09.2026): der eingefaltete KOPFBLOCK, dann
   * der Einleitungstext, dann die POSITIONEN, dann der Ausleitungstext. Bis dahin standen
   * die Positionen auch hier ganz unten — hinter sechs Kopffeldern, dem aufklappbaren
   * Lieferort und zwei Textblöcken —, obwohl an ihnen die eigentliche Arbeit geschieht.
   *
   * Eine NOTIZ führt die Rechnung nicht (anders als das Angebot); über den Positionen steht
   * hier also nur der eine Text.
   */
  function entwurfsKasten(): ReactElement {
    const e = entwurf ?? entwurfAus(r);
    function setze(feld: keyof Entwurf) {
      return (wert: string) =>
        setEntwurf((v) => ({ ...(v ?? entwurfAus(r)), [feld]: wert }));
    }
    const kf = kopfFormular;
    /** Befunde je Feld — die des Servers und die des Parteien-Weges (sie stehen in ihrem
     *  Block); daraus zählt die Kopfzeile des eingeklappten Blocks ihre Fehler. */
    const fehlerJeFeld: Record<string, string> = { ...kf.feldFehler, ...parteien.befunde };
    /** Im fremden Stand und während des Speicherns ist alles in der Maske zu — auch die
     *  Bausteine, die selbst schreiben (Positions-Editor) oder selbst zeichnen (Parteien). */
    const zu = kf.zustand !== "bereit";
    return (
      /* Das Raster der Maske (`me-formular`, ME-Etappe 3, Aufgabe 5) — wie an Angebot und
         Auftrag um die GANZE Maske: Die Hinweiszeile „fremder Stand" bleibt sichtbar, auch
         wenn der Kopfblock zu ist, und `zumErstenFehler` sucht über alle Blöcke. */
      <Formular beschriftung="oben" {...kf.formularProps}
        hinweis={fremderStandHinweis(kf, "Deine Eingaben stehen noch da — „Neu laden“ "
          + "holt den aktuellen Stand und ersetzt sie.")}>
      <div className="krg__maske">
        {/* Die Kopfzeile zeigt auch EINGEKLAPPT, woran man diesen Beleg erkennt: sein
            Rechnungsdatum (das Feld gleich darunter — an ihm hängen Nummer, Steuersatz und
            Jahresabgrenzung) und seinen Betrag.

            BRUTTO und nicht netto, mit derselben Begründung wie in der Datenzeile darüber:
            Was eine Rechnung kostet, ist ihr Bruttobetrag. Anders als an Angebot und
            Auftrag steht der Betrag hier nicht wörtlich in einem Feld darin — die Rechnung
            hat gar kein Betragsfeld, ihre Summen rechnet ausschliesslich der Server aus den
            Positionen. Genau das sagt die Beschriftung. */}
        <Kopfblock faltung={kopf} titel={kopfTitel(e)}
          betragLabel="Bruttobetrag (aus den Positionen)"
          betrag={formatBetrag(r.grossAmount, r.currency)}
          inhaltId={KOPF_INHALT_ID}
          fehler={kopfFehlerZahl(RECHNUNG_KOPFFELDER, fehlerJeFeld)}>
          <div className="krg__felder">
            <Feld id="krg-datum" label="Rechnungsdatum" typ="date" bearbeitbar
              wert={e.invoiceDate} onChange={setze("invoiceDate")}
              {...feldFehlerProp(fehlerJeFeld, "invoiceDate")} />
            <div className="krg__feld">
              {/* NUR-LESEND, und deshalb als Text und nicht als Feld: Der Satz ist der
                  Snapshot der bei der Ableitung aufgelösten Kondition. Fehlt er (z. B. weil
                  die Kundenbeziehung archiviert war), steht das ausdrücklich da — sonst
                  sähe die Zeile aus wie „noch nicht geladen".

                  KEIN `<label for>`: `for` verlangt ein labelbares Element, auf einem
                  Absatz greift die Zuordnung weder für den Klick noch für den
                  Screenreader. Die Beschriftung hängt deshalb über `aria-labelledby` am
                  Absatz. */}
              <span className="krg__label" id="krg-zahlung-label">Zahlungsbedingung</span>
              <p className="krg__wert" aria-labelledby="krg-zahlung-label">
                {r.paymentTermText ?? "kein Zahlungssatz hinterlegt"}
              </p>
            </div>
            <Feld id="krg-von" label="Leistungszeitraum von" typ="date" bearbeitbar
              wert={e.servicePeriodStart} onChange={setze("servicePeriodStart")}
              {...feldFehlerProp(fehlerJeFeld, "servicePeriodStart")} />
            <Feld id="krg-bis" label="Leistungszeitraum bis" typ="date" bearbeitbar
              wert={e.servicePeriodEnd} onChange={setze("servicePeriodEnd")}
              {...feldFehlerProp(fehlerJeFeld, "servicePeriodEnd")} />
            {/* me-auswahl (ME-Etappe 3, Aufgabe 5) statt des rohen `<select>` — die Kennung
                bleibt (`kopf.zeigeFeld` fokussiert über sie). */}
            <Auswahl id="krg-rechnungsformat" name="krg-rechnungsformat"
              beschriftung="Rechnungsformat" wert={e.invoiceFormat}
              {...feldFehlerProp(fehlerJeFeld, "invoiceFormat")}
              onGeaendert={(d) => setze("invoiceFormat")(d.wert)}>
              {RECHNUNGSFORMATE.map((f) => (
                <option key={f.wert} value={f.wert}>{f.text}</option>
              ))}
            </Auswahl>
          </div>

          {/* Der Kopfblock PARTEIEN — `key` am Beleg: Ein Wechsel der bearbeiteten
              Rechnung baut ihn neu auf, statt den Entwurf der vorigen weiterzureichen.

              HIER STAND der Aufklapper „Abweichender Lieferort" mit fünf Textfeldern. Er
              ist mit Adressen Etappe 3, Aufgabe 6 gefallen: Der Lieferort ist eine
              ADRESSWAHL geworden, und das Render-Modell nimmt seit Aufgabe 5 die gewählte
              DELIVERY-Adresse VOR den getippten Feldern. Beides nebeneinander hiesse: Die
              Maske zeigt die eine Anschrift, das PDF druckt die andere. */}
          <BelegParteien key={`${r.id}-${String(standNeuLaden)}`} client={client}
            art="invoice" customerId={r.customerId} vorgangId={r.id}
            idPraefix={KOPF_PRAEFIX} verdeckt={zu}
            befunde={fehlerJeFeld} onEntwurf={parteien.melde} />
        </Kopfblock>

        {/* Der Einleitungstext steht ÜBER den Positionen — er leitet sie ein, und in der
            Maske soll er dort stehen, wo er auf dem Beleg steht. */}
        <div className="krg__texte">
          <PlatzhalterTextfeld id="krg-intro" label="Einleitungstext"
            wert={e.introText}
            onChange={(wert) =>
              setEntwurf((v) => ({ ...(v ?? entwurfAus(r)), introText: wert }))}
            variablen={umfeld.platzhalter} werte={vorschauWerte()} />
        </div>

        <div className="krg__positionen">
          <h3 className="krg__abschnitt">Positionen</h3>
          {/* DERSELBE Editor wie an Angebot und Auftrag — nur der Bezug (`bezug.art`)
              unterscheidet die Einsatzorte. Jeder Schritt dreht die Version der RECHNUNG
              weiter; `positionenGeaendert` trägt sie in die Liste des Rahmens. */}
          <PositionsEditor
            // „Neu laden" im fremden Stand hängt den Editor neu auf — er führt seinen Stand
            // selbst und lädt so den Serverstand.
            key={`${r.id}-${String(standNeuLaden)}`}
            client={client} bezug={{ art: "invoice", customerId: kundeId, vorgangId: r.id }}
            companyId={r.companyId} version={r.version} waehrung={r.currency} gesperrt={zu}
            onVersionChange={positionenGeaendert} />
          {/* Der Herkunftshinweis (Ruling T14, Paket Teilrechnungen): gebaut aus dem, was
              die Rechnung OHNEHIN trägt — der Auftragsnummer im Kopf, der Bestellposition
              JEDER Zeile (oben, lesend am Editor) und dem Positionstext, der das
              Leistungsjahr bereits nennt. Ein eigener Weg nur für „Pos. 3" wäre ein
              fünfter Weg für eine Zeile Text, und die Zuordnung selbst ist ausdrücklich
              nicht bearbeitbar (YAGNI, Spec). */}
          <p className="krg__herkunft">
            Die Positionen dieser Rechnung gehören zum Auftrag
            {" "}<strong>{r.salesOrderNumber}</strong>; ihre Zuordnung auf die
            Auftragspositionen und Leistungszeiträume entsteht beim Rechnungsvorschlag und
            lässt sich hier nicht ändern.
          </p>
        </div>

        {/* Der Ausleitungstext schliesst den Beleg ab — also steht er hinter dem, was er
            abschliesst. */}
        <div className="krg__texte">
          <PlatzhalterTextfeld id="krg-outro" label="Ausleitungstext"
            wert={e.outroText}
            onChange={(wert) =>
              setEntwurf((v) => ({ ...(v ?? entwurfAus(r)), outroText: wert }))}
            variablen={umfeld.platzhalter} werte={vorschauWerte()} />
        </div>

        {/* Die Sammelmeldung steht am Ende der Maske, dicht über der Fußleiste: Dort wurde
            „Speichern" gedrückt. Befunde an einzelnen Feldern stehen AM FELD; betrifft
            einer ein Feld im eingeklappten Kopfblock, hat die Maske ihn aufgeklappt. */}
        {kf.meldung !== null && <Hinweis art="fehler">{kf.meldung}</Hinweis>}
      </div>
      </Formular>
    );
  }

  /**
   * Der Titel in der Kopfzeile des eingefalteten Blocks: das RECHNUNGSDATUM des Entwurfs,
   * deutsch geschrieben.
   *
   * Warum das Datum und nicht die Belegnummer: Ein Entwurf hat keine (sie entsteht erst
   * beim Festschreiben), und „zu Auftrag …" steht bereits in der Überschrift des Panels.
   * Das Datum dagegen ist die folgenreichste Angabe im Block — an ihm hängen die Nummer,
   * die Zeitscheibe des Steuersatzes und die Jahresabgrenzung. Es ist also genau das, was
   * man sehen will, wenn der Block zu ist.
   *
   * Aus dem ENTWURF und nicht aus dem Beleg: Getippt wird im Feld, und die Kopfzeile soll
   * mitgehen — sonst behauptete sie einen Stand, den das Feld darunter schon widerlegt.
   */
  function kopfTitel(e: Entwurf): string {
    return e.invoiceDate.trim() === ""
      ? "ohne Rechnungsdatum" : formatDatum(e.invoiceDate);
  }

  /**
   * Die NUR-LESENDE Fassung: kein Feld, kein Schreibknopf, keine Editor-Instanz. Der
   * Unterschied ist bewusst nicht bloss ein `disabled` — ein gesperrtes Eingabefeld stünde
   * weiter im Baum, und dieselbe Maske wäre eine Umbauwelle später wieder scharf.
   */
  function belegKasten(): ReactElement {
    const waehrung = r.currency;
    return (
      <>
        <Hinweis art="info" className="krg__gesperrt">
          {r.status === "CANCELLED"
            ? "Diese Rechnung ist storniert — nur lesend."
            : "Diese Rechnung ist festgeschrieben — nur lesend."}
        </Hinweis>
        <dl className="krg__werte">
          <div className="krg__wertpaar">
            <dt>Rechnungsnummer</dt>
            <dd>{r.invoiceNumber ?? "—"}</dd>
          </div>
          <div className="krg__wertpaar">
            <dt>Rechnungsformat</dt>
            <dd>{RECHNUNGSFORMATE.find((f) => f.wert === r.invoiceFormat)?.text
              ?? r.invoiceFormat}</dd>
          </div>
          <div className="krg__wertpaar">
            <dt>Rechnungsdatum</dt>
            <dd>{r.invoiceDate === null ? "—" : formatDatum(r.invoiceDate)}</dd>
          </div>
          {/* Die Fälligkeit (dueDate, Migration 011): von der Festschreibung aus der
              Zahlungskondition gerechnet und mit allem eingefroren. Der Strich steht für
              den fachlich gedeckten Fall „Kondition ohne Stammsatz-Verweis" — dann gibt
              es keine Fälligkeit. */}
          <div className="krg__wertpaar">
            <dt>Fällig am</dt>
            <dd>{r.dueDate === null ? "—" : formatDatum(r.dueDate)}</dd>
          </div>
          <div className="krg__wertpaar">
            <dt>Leistungszeitraum</dt>
            <dd>{zeitraumText()}</dd>
          </div>
          {/* HIER STAND „Abweichender Lieferort" — eine Zeile aus den fünf
              `delivery*`-Spalten der Rechnung (BG-13, Migration 015). Sie ist mit Adressen
              Etappe 3 gefallen: Der Lieferort ist eine Adresswahl geworden, die Spalten
              bleiben bis Migration 024 leer stehen. Was an ihre Stelle tritt, steht unter
              dieser Liste — der ganze PARTEIEN-Block, nicht nur der Lieferort. */}
          <div className="krg__wertpaar">
            <dt>Zahlungsbedingung</dt>
            <dd>{r.paymentTermText ?? "kein Zahlungssatz hinterlegt"}</dd>
          </div>
          {/* Einleitungs- und Ausleitungstext GESETZT, nicht als Rohtext (Entscheid
              05.09.2026): Das hier ist die Lese-Ansicht einer festgeschriebenen Rechnung —
              was im Beleg fett steht oder eine Aufzählung ist, soll auch hier so aussehen.
              Gerendert mit dem vorhandenen `renderMarkdown` (markdown.tsx, sanitisierend,
              externe Bilder als Link) — demselben, der Notizen und Artikelbeschreibungen
              setzt. Die EINGABE oben bleibt Rohtext; nur Lese- und Vorschau-Ansichten
              rendern. */}
          {r.introText !== null && r.introText !== "" && (
            <div className="krg__wertpaar krg__wertpaar--breit">
              <dt>Einleitungstext</dt>
              <dd className="krg__belegtext">{renderMarkdown(r.introText)}</dd>
            </div>
          )}
          {r.outroText !== null && r.outroText !== "" && (
            <div className="krg__wertpaar krg__wertpaar--breit">
              <dt>Ausleitungstext</dt>
              <dd className="krg__belegtext">{renderMarkdown(r.outroText)}</dd>
            </div>
          )}
        </dl>

        {/*
          Die PARTEIEN des Belegs — lesend (Adressen Etappe 3, Fixrunde 1 zu Aufgabe 7).
          Sie stehen HINTER der Werteliste und nicht als Zeile darin: Der Baustein bringt
          seine eigene Überschrift und seine eigene `dl` mit, und ein `<div>` mit `<h4>`
          innerhalb einer `<dl>` wäre keine Definitionsliste mehr.

          `nurLesend`, obwohl dieser Kasten ohnehin nur an einem nicht mehr bearbeitbaren
          Beleg gezeichnet wird (`!istEntwurf(r)`, und das heisst `eingefroren`): Die
          Zusage „ausserhalb des Panels wird nichts geschrieben" soll am Einbauort stehen
          und nicht davon abhängen, was der Server über den Status meldet.

          `key` am Beleg — derselbe Grund wie im Panel: Ein Wechsel der Rechnung baut den
          Block neu auf, statt den Stand der vorigen weiterzureichen.
        */}
        <BelegParteien key={r.id} client={client} art="invoice"
          customerId={r.customerId} vorgangId={r.id}
          idPraefix={`${KOPF_PRAEFIX}-lesend`} nurLesend />

        <h3 className="krg__abschnitt">Positionen</h3>
        {positionen === null
          ? <p className="krg__hinweis">Positionen werden geladen …</p>
          : positionsTabelle(positionen, waehrung)}
      </>
    );
  }

  function zeitraumText(): string {
    if (r.servicePeriodStart === null && r.servicePeriodEnd === null) return "—";
    const von = r.servicePeriodStart === null ? "offen" : formatDatum(r.servicePeriodStart);
    const bis = r.servicePeriodEnd === null ? "offen" : formatDatum(r.servicePeriodEnd);
    return `${von} bis ${bis}`;
  }

  /** Die Zeilen und Summen eines festgeschriebenen Belegs — alles GERECHNET vom Server. */
  function positionsTabelle(stand: PositionsStand, waehrung: string): ReactElement {
    return (
      <div className="krg__positionsliste">
        {/* me-tabelle (ME-Etappe 3, Aufgabe 5): Rahmen, Kopfzeile, Zebra und das
            waagerechte Rollen bringt das Element; Zahlen rechtsbündig über
            `data-ausrichtung` an th UND td. */}
        <Tabelle>
          <table className="krg__tabelle" aria-label="Positionen der Rechnung">
            <thead>
              <tr>
                <th scope="col">Bezeichnung</th>
                <th scope="col" data-ausrichtung="rechts">Menge</th>
                <th scope="col">Einheit</th>
                <th scope="col" data-ausrichtung="rechts">Einzelpreis</th>
                <th scope="col" data-ausrichtung="rechts">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {stand.items.map((i) => zeile(i, stand, waehrung))}
            </tbody>
          </table>
        </Tabelle>
        <dl className="krg__summen">
          {stand.steuersummen.jeSatz.map((s) => (
            <div key={s.taxRateId ?? "ohne"} className="krg__wertpaar">
              <dt>{s.name} ({formatMenge(s.ratePercent)} %)</dt>
              <dd className="ki-num">{formatDezimalBetrag(s.steuer, waehrung)}</dd>
            </div>
          ))}
          <div className="krg__wertpaar">
            <dt>Nettosumme</dt>
            <dd className="ki-num">
              {formatDezimalBetrag(stand.steuersummen.gesamtNetto, waehrung)}
            </dd>
          </div>
          <div className="krg__wertpaar krg__wertpaar--stark">
            <dt>Bruttobetrag</dt>
            <dd className="ki-num">
              {formatDezimalBetrag(stand.steuersummen.gesamtBrutto, waehrung)}
            </dd>
          </div>
        </dl>
      </div>
    );
  }

  function zeile(i: Position, stand: PositionsStand, waehrung: string): ReactElement {
    const netto = stand.zeilennettos[i.id];
    const zwischen = stand.zwischensummen[i.id];
    return (
      <tr key={i.id} className={`krg__pos krg__zeile-${i.kind.toLowerCase()}`}>
        <td style={{ paddingLeft: `${8 + i.level * 20}px` }}>{i.name}
          {i.description !== null && i.description !== "" && (
            <span className="krg__beschreibung">{i.description}</span>
          )}
        </td>
        <td className="ki-num" data-ausrichtung="rechts">
          {i.kind === "ITEM" ? formatMenge(i.quantity) : ""}
        </td>
        <td>{i.kind === "ITEM" ? (i.unit ?? "") : ""}</td>
        <td className="ki-num" data-ausrichtung="rechts">
          {i.kind === "ITEM" ? formatBetrag(i.netUnitPrice, waehrung) : ""}
        </td>
        <td className="ki-num" data-ausrichtung="rechts">
          {/* Eine TEXTZEILE hat keinen Betrag — dort bleibt die Zelle leer statt „0,00 €"
              zu behaupten (dieselbe Regel wie im Editor). */}
          {i.kind === "SUBTOTAL"
            ? (zwischen === undefined ? "" : formatDezimalBetrag(zwischen, waehrung))
            : (netto === undefined ? "" : formatDezimalBetrag(netto, waehrung))}
        </td>
      </tr>
    );
  }

  /**
   * Die Ausgabewege, der Ablagestand und das Render-Modell als aufklappbarer JSON-Block —
   * der Arbeitsstand, bis die Engine überall PDFs liefert. Ein `<details>`-Element und KEIN
   * neues Paket: Ein JSON-Betrachter mit Faltung und Farbe wäre eine Abhängigkeit für eine
   * Ansicht, die mit der Anbindung der Engine wieder verschwindet.
   */
  function renderModellBlock(): ReactElement {
    return (
      <>
      <section className="krg__engine" aria-label="Beleg ausgeben">
        {/* Auch diese Wege liegen hinter dem Panel, solange es steht — und sind deshalb
            gesperrt, statt als Knöpfe dazustehen, die niemand drücken kann. */}
        <div className="krg__formular-aktionen">
          {/* Die Vorschau — NEBEN den Ausgabewegen, AUCH an der festgeschriebenen und an der
              stornierten Rechnung (Spec: das exakte Blatt, ohne ENTWURF-Kennzeichen — der
              Baustein liest `entwurf` aus der Antwort, hier steht dafür KEINE Verzweigung). */}
          <KiKnopf deaktiviert={bearbeitung} onClick={() => setVorschauOffen(true)}>
            Vorschau
          </KiKnopf>
          <KiKnopf deaktiviert={bearbeitung} onClick={() => { void rendern("PDF_A3"); }}>
            {AUSGABEWEGE.PDF_A3.knopf}
          </KiKnopf>
          {/* Die E-Rechnungs-Wege NUR an der festgeschriebenen Rechnung: Am Entwurf fehlt
              die Rechnungsnummer (BT-1), an der stornierten die Forderung — die API weist
              beides mit 422 ab. Ein Knopf, der immer scheitert, ist schlechter als
              keiner. */}
          {r.status === "ISSUED" && E_RECHNUNGS_PROFILE.map((profil) => (
            <KiKnopf key={profil} deaktiviert={bearbeitung}
              onClick={() => { void rendern(profil); }}>
              {AUSGABEWEGE[profil].knopf}
            </KiKnopf>
          ))}
          {/* Die Ablage in der Kundenakte — ebenfalls NUR an der festgeschriebenen
              Rechnung: Am Entwurf gibt es keine Belegnummer (und damit keinen Dateinamen),
              an der stornierten bleibt eine vorhandene Ablage stehen, eine neue entsteht
              nicht. Beides weist die API mit 422 ab. Die Beschriftung sagt, was der Klick
              tut: nachholen oder erneuern. */}
          {r.status === "ISSUED" && (
            <KiKnopf deaktiviert={bearbeitung || belegt}
              onClick={() => { void inAkteAblegen(); }}>
              {ablage === null || ablage === undefined
                ? "PDF in Akte ablegen" : "PDF in der Akte erneuern"}
            </KiKnopf>
          )}
        </div>
        {/* Die Zeile sagt den STAND — und zwar beide Ausgänge: Liegt das PDF in der Akte,
            wann; liegt es NICHT dort, dass es fehlt. Der zweite Fall ist der wichtige: Er
            ist die einzige sichtbare Spur eines Automatiklaufs, der nach der Festschreibung
            geklemmt hat. Solange die Abfrage nicht beantwortet ist (`undefined`), steht
            KEINE der beiden Zeilen — behaupten lässt sich dann nichts. */}
        {r.status !== "DRAFT" && ablage !== undefined && (
          /* Als `Hinweis` (ME-Etappe 3, Aufgabe 5): Fehlt die Ablage, ist das die Spur
             eines geklemmten Automatiklaufs — Warnung; liegt sie, eine ruhige Auskunft. */
          ablage === null
            ? (
              <Hinweis art="warnung" className="krg__ablage">
                Noch nicht in der Akte abgelegt.
                {r.status === "ISSUED"
                  && " Mit „PDF in Akte ablegen“ lässt sich das nachholen."}
              </Hinweis>
            )
            : (
              <Hinweis art="info" className="krg__ablage">
                Abgelegt in der Akte am {formatDatum(ablage.filedAt)} als „{ablage.fileName}"
                — zu finden im Reiter „Dateien" dieses Kunden.
              </Hinweis>
            )
        )}
        {/*
          * DER DATEV-STAND dieses Belegs (Migration 028, Aufgabe 9). Dieselbe Bauart wie
          * die Ablage-Zeile darüber: ein eigener Weg (`getDatevStandZuRechnung`), beim
          * Aufschlagen einer festgeschriebenen Rechnung einmal geholt, KEIN Feld an der
          * Rechnungsgestalt.
          *
          * Sie sagt BEIDE Ausgänge — das ist der Punkt: „übergeben am …" beruhigt, ein
          * Fehlertext (auch am noch offenen Posten, Ruling D6: „Debitor fehlt") ist die
          * einzige sichtbare Spur eines Belegs, der in der Buchhaltung nie ankommt. Solange
          * die Abfrage nicht beantwortet ist (`undefined`), steht keine Zeile; `null` heisst
          * „keine Kopplung" und schweigt ebenfalls — eine Zeile über einen Weg, den dieses
          * Unternehmen gar nicht geht, wäre Lärm.
          *
          * `dtv__zeile` ist derselbe Blockname wie im Bereich Schnittstellen → DATEV und an
          * der Kundenbeziehung — hier als reine Kennzeichnung ohne eigene Regel (die drei
          * Stellen teilen sich das WORT, nicht zwingend eine Zeile CSS); ein Import von
          * views/DatevKopplung.css unterbliebe absichtlich: `components/` reicht nicht nach
          * `views/` hinüber (Schichtung dieses Pakets).
          */}
        {/* Als `Hinweis`: Der Fehlertext am offenen Posten ist eine Warnung (der Beleg kommt
            so nie in der Buchhaltung an), „bestätigt" ein Erfolg, alles andere Auskunft. */}
        {r.status !== "DRAFT" && datev !== undefined && datev !== null && (
          <Hinweis className="dtv__zeile"
            art={datev.status === "CONFIRMED" ? "ok"
              : datev.status !== "EXPORTED" && datev.errorText !== null ? "warnung" : "info"}>
            {datev.status === "CONFIRMED"
              ? `DATEV: bestätigt am ${formatDatum(datev.confirmedAt!)}`
              : datev.status === "EXPORTED"
                ? `DATEV: übergeben am ${formatDatum(datev.exportedAt!)}`
                : datev.errorText !== null
                  ? `DATEV: ${datev.errorText}`
                  : "DATEV: noch nicht übergeben"}
          </Hinweis>
        )}
        {engineMeldung !== null && (
          <Hinweis art={PDF_HINWEIS_ART[engineMeldung.art]} className="krg__engine-meldung">
            {engineMeldung.text}
          </Hinweis>
        )}
        {/* Die Feldbefunde als LISTE — jeder Pfad einzeln und ungekürzt, zum Abhaken, und
            das ist die EINZIGE Stelle, an der sie stehen (Befund 05.09.2026: vorher zählte
            der Meldungssatz sie ein zweites Mal auf, gedeckelt und mitten im Pfad
            abgebrochen). NEBEN der Meldung und nicht in ihr: Die trägt `role="alert"` und
            muss ein `<p>` bleiben (ein `<ul>` darf dort gar nicht hinein), und die Ansage
            soll ein SATZ sein, keine vorgelesene Liste. */}
        {engineMeldung?.felder !== undefined && engineMeldung.felder.length > 0 && (
          <ul className="krg__fehlerfelder" aria-label="Fehlende Pflichtwerte">
            {engineMeldung.felder.map((f) => (
              <li key={f.field}>
                <code>{f.field}</code>: {f.message}
              </li>
            ))}
          </ul>
        )}

        {/* Der JSON-Block hängt an `<details>`: Das Modell wird erst beim AUFKLAPPEN geholt
            (onToggle), und zugeklappt steht es niemandem im Weg. */}
        <details className="krg__modell"
          onToggle={(e) => {
            if (e.currentTarget.open) void renderModellLaden();
          }}>
          <summary className="krg__modell-kopf">Render-Modell (Arbeitsstand)</summary>
          <p className="krg__hinweis krg__hinweis--klein">
            Das JSON, das die Render-Engine zum Setzen des Belegs bekommt (Vertrag
            <code> kundeninfo-beleg/1</code>). Die Texte stehen ROH mit ihren Platzhaltern;
            aufgelöst wird bei der Engine.
          </p>
          {renderFehler !== null && <Hinweis art="fehler">{renderFehler}</Hinweis>}
          {renderModell === null
            ? <p className="krg__hinweis">Noch nicht geladen.</p>
            : (
              <pre className="krg__json" aria-label="Render-Modell als JSON">
                {JSON.stringify(renderModell, null, 2)}
              </pre>
            )}
        </details>
      </section>
      {vorschauOffen && (
        <Belegvorschau
          // Der Titel trägt die Belegart (M2, Schlussrunde): Derselbe Baustein dient auch
          // Angebot und Auftrag, und die nackte Nummer liesse dort nicht erkennen, um
          // welche Belegart es sich handelt.
          titel={r.invoiceNumber === null
            ? "Rechnungsentwurf · Vorschau" : `Rechnung ${r.invoiceNumber} · Vorschau`}
          // Fassung aus Version UND Status: Das Festschreiben ändert das Blatt (Nummer,
          // Datum, kein ENTWURF), und es ändert auch die Version — aber der Status sagt es
          // DEUTLICHER, und beides zusammen kostet nichts (Ruling K6).
          fassung={`${r.version}:${r.status}`}
          laden={() => client.vorschauInvoice(kundeId, r.id)}
          // Der PDF-Knopf der Vorschau geht denselben Weg wie „Als PDF" (PROFIL PDF_A3) —
          // an der Rechnung gibt es mehrere Ausgabewege, die Vorschau zeigt ein PDF, also
          // bekommt sie GENAU diesen.
          aufPdf={() => { void rendern("PDF_A3"); }}
          onSchliessen={() => setVorschauOffen(false)} />
      )}
      </>
    );
  }
}
